import { PanelComponentKeyType } from 'config/panelConfig';
import {
  alignAxis,
  captureImage,
  cross,
  dot,
  gecalarDataFromVolume,
  getOrthoNormal,
  getVolumeFromSeriesId,
  length,
  normalize,
  rotateAroundAxis,
  scalePoint3,
} from '@features/analysis/rendering/Panels/Renderers/CornerstoneManager';
import {
  BufferPool,
  BufferPoolItem,
  ContextKey,
  ContextPool,
  CPRContext,
  fitActorToCanvas,
  mergeActorBounds,
  resetCPRView,
  rotateByDegreesAtCenter,
  SurfaceContext,
  updatelabel,
  updateResetCamera,
  VRContext,
} from '@features/analysis/rendering/Panels/Renderers/vtkManagers';
import {
  BranchInfo,
  getPerpendicularLine,
  numbersByOrder,
} from 'utils/FunctionUtilites';
import {
  createCPRActorAndMapper,
  handleCPRAngle,
} from 'utils/vtkUtilites';
import { radiansFromDegrees } from '@kitware/vtk.js/Common/Core/Math';
import vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import type vtkPolyData from '@kitware/vtk.js/Common/DataModel/PolyData';
import vtkImageSlice from '@kitware/vtk.js/Rendering/Core/ImageSlice';
import vtkGenericRenderWindow from '@kitware/vtk.js/Rendering/Misc/GenericRenderWindow';
import { vec3 } from 'gl-matrix';

export type ViewSource =
  | { type: 'vtk'; renderWindow: any }
  | { type: 'canvas'; canvas: HTMLCanvasElement }
  | { type: 'element'; element: HTMLDivElement; rasterize: RasterizeFn };

export type RasterizeFn = (el: HTMLElement) => Promise<HTMLCanvasElement>;

export interface CaptureOptions {
  format?: 'image/png' | 'image/jpeg';
  quality?: number;
  scale?: number;
}

function waitForNextPaint(frames: number = 1): Promise<void> {
  return new Promise((resolve) => {
    const tick = (remaining: number) => {
      requestAnimationFrame(() => {
        if (remaining <= 1) {
          resolve();
          return;
        }
        tick(remaining - 1);
      });
    };
    tick(Math.max(1, frames));
  });
}

// vtkCapture.ts
export async function captureCPRPNG(renderWindow: any): Promise<string> {
  if (!renderWindow) throw new Error('renderWindow required');
  const view = renderWindow.getViews?.()[0];

  // 1) VTK 내장 API (가장 안전)
  const cap = (view && view.captureNextImage) || renderWindow.captureNextImage;
  if (typeof cap === 'function') {
    const p = cap.call(view ?? renderWindow, 'image/png');
    renderWindow.render();
    const img = await p;
    const url = typeof img === 'string' ? img : img?.src;
    if (url) return url;
  }

  // 2) readPixels
  try {
    renderWindow.render();
    await new Promise((r) => requestAnimationFrame(() => r(null)));
    const get3D = (view && view.get3DContext) || renderWindow.get3DContext;
    const gl: WebGLRenderingContext | WebGL2RenderingContext | undefined =
      get3D?.call(view ?? renderWindow);
    if (gl) {
      const w = (gl as any).drawingBufferWidth;
      const h = (gl as any).drawingBufferHeight;
      const pixels = new Uint8Array(w * h * 4);
      gl.readPixels(
        0,
        0,
        w,
        h,
        (gl as any).RGBA,
        (gl as any).UNSIGNED_BYTE,
        pixels,
      );

      const out = document.createElement('canvas');
      out.width = w;
      out.height = h;
      const ctx2d = out.getContext('2d')!;
      const img = ctx2d.createImageData(w, h);
      for (let y = 0; y < h; y++) {
        const src = (h - 1 - y) * w * 4; // Y-flip
        img.data.set(pixels.subarray(src, src + w * 4), y * w * 4);
      }
      ctx2d.putImageData(img, 0, 0);
      return out.toDataURL('image/png');
    }
  } catch {}

  // 3) 최후: 캔버스 직렬화 (preserveDrawingBuffer:true 필요)
  const canvas: HTMLCanvasElement | undefined =
    view?.getCanvas?.() ?? view?.getGLCanvas?.();
  if (canvas) {
    renderWindow.render();
    await new Promise((r) => requestAnimationFrame(() => r(null)));
    return canvas.toDataURL('image/png');
  }

  throw new Error('VTK capture failed');
}

import { getCanvasCaptureProvider } from '@root/store';
import { configStore } from 'config/configStore';
import vtkVolume from '@kitware/vtk.js/Rendering/Core/Volume';
import vtkImageCPRMapper from '@kitware/vtk.js/Rendering/Core/ImageCPRMapper';
import {
  CPRExportResult,
  exportCPRHU,
  exportCPRPNGStandaloneMultiAngle,
  exportCPRPNGStandaloneMultiAngleWithLabel,
} from 'utils/CPRUtilities/CPRUtilities';
import { setAxesLinesVisibility } from '@features/view/axesLines';
import { Vector2, Vector3 } from '@kitware/vtk.js/types';
import { MutableRefObject } from 'react';
import vtkRenderer from '@kitware/vtk.js/Rendering/Core/Renderer';
import {
  AnalysisType,
  ChamberViewProps,
  getActorNameByAnalysisType,
  getSegmentationAnalysisType,
  PanelState,
  ThicknessMethodType,
  VOI,
} from 'config/analysisConfig';
import vtkCamera from '@kitware/vtk.js/Rendering/Core/Camera';
import {
  AutoCaptureMode,
  Send_PACS_Data,
} from '@features/analysis/analysisModal/AutoCaptureModal/AutoCaptureModal';
import { PanelStates } from 'config/analysisConfig';
import { runWorkerTask } from '@features/analysis/rendering/Panels/workerPool';
import { math } from '@cornerstonejs/tools/dist/esm/utilities';
import {
  runSegmentationVolume,
  setPickingForVolume,
  updateEFVolumeMapper,
} from '@features/view/VRUtilities';
import {
  applyPoolCaptureCamera,
  applyScaledCameraPan,
  centerLineRender,
  getOriCamera,
  setActorVisible,
} from '@features/view/Shared3DUtilities';
import vtkRenderWindow from '@kitware/vtk.js/Rendering/Core/RenderWindow';
import { CPRInfo } from 'utils/PacsUtilities';
import { nodeNames, volumeNames } from 'config/render3DConfig';

declare global {
  interface Window {
    capture?: {
      region(rect: {
        x: number;
        y: number;
        width: number;
        height: number;
      }): Promise<string>;
    };
  }
}

/** 메인: el을 기준으로 VTK 캔버스(바닥) + DOM 오버레이를 합성하여 PNG dataURL 반환 */
export async function screenshotElement(el: HTMLDivElement): Promise<string> {
  const rect = el.getBoundingClientRect();
  const dpr = window.devicePixelRatio || 1;

  // 0) Electron이면 네이티브 영역 캡처
  if (window.capture?.region) {
    return window.capture.region({
      x: Math.round(rect.left),
      y: Math.round(rect.top),
      width: Math.round(rect.width),
      height: Math.round(rect.height),
    });
  }

  // 1) 출력 캔버스 준비
  const out = document.createElement('canvas');
  out.width = Math.max(1, Math.round(rect.width * dpr));
  out.height = Math.max(1, Math.round(rect.height * dpr));
  const ctx = out.getContext('2d')!;

  // 2) (중요) 먼저 VTK/Cornerstone 등 "등록된 캔버스"를 그린다 = 바닥
  await drawRegisteredCanvases(out, el, { descendantsOnly: true });

  // 1-α) 배경색 채우기(모달일 때 유용). 바닥 위에 'destination-over'로 깔기.
  const bg = resolveOpaqueBackground(el);
  if (bg) {
    const ctx = out.getContext('2d')!;
    ctx.save();
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.restore();
  }

  // 3) 그 다음 DOM 오버레이만 투명 비트맵으로 캡처해서 얹는다 (canvas 제외)
  const overlay = await captureOverlayTransparent(el, dpr);
  if (overlay) {
    const img = new Image();
    img.src = overlay;
    await img.decode();
    ctx.drawImage(img, 0, 0, out.width, out.height);
  } else {
    // 오버레이 캡처가 불가하면, 최소한 배경을 흰색으로 (원하면 주석 처리)
    // ctx.fillStyle = '#fff';
    // ctx.globalCompositeOperation = 'destination-over';
    // ctx.fillRect(0, 0, out.width, out.height);
  }

  // 4) 결과
  return out.toDataURL('image/png');
}

/** DOM 오버레이만(텍스트/SVG/HTML) 투명 배경으로 캡처. 내부의 <canvas>는 제외 */
async function captureOverlayTransparent(
  el: HTMLElement,
  pixelRatio: number,
): Promise<string | null> {
  try {
    const { toPng } = await import('html-to-image');

    // 캔버스/비디오 등은 제외하고, 레이블/SVG/텍스트만 캡처
    const url = await toPng(el, {
      pixelRatio,
      cacheBust: true,
      skipAutoScale: true,
      backgroundColor: 'rgba(0,0,0,0)',
      style: { background: 'transparent' },
      filter: (node: any) => {
        if (node instanceof HTMLCanvasElement) return false; // ← 핵심: 캔버스 제외
        // 필요 시 특정 요소 제외: (data-capture-exclude, .exclude 등)
        if (
          node instanceof HTMLElement &&
          node.dataset.captureExclude === 'true'
        )
          return false;
        return true;
      },
    } as any);

    return url;
  } catch (e) {
    // html-to-image 불가 (정책/리소스/CORS 등)
    return null;
  }
}

async function drawRegisteredCanvases(
  out: HTMLCanvasElement,
  el: HTMLElement,
  opts: { descendantsOnly?: boolean } = {},
) {
  const { descendantsOnly = false } = opts;
  const ctx = out.getContext('2d')!;
  const dpr = window.devicePixelRatio || 1;
  const r = el.getBoundingClientRect();

  let canvases = Array.from(
    document.querySelectorAll('canvas'),
  ) as HTMLCanvasElement[];

  // ★ 핵심: el 하위만 선택 (모달 밖 캔버스 배제)
  if (descendantsOnly) {
    canvases = canvases.filter((cv) => el.contains(cv));
  }

  // 화면 레이어 순서에 가깝게 z-index로 정렬(하층→상층)
  canvases.sort((a, b) => {
    const za = parseFloat(getComputedStyle(a).zIndex || '0') || 0;
    const zb = parseFloat(getComputedStyle(b).zIndex || '0') || 0;
    return za - zb;
  });

  for (const cv of canvases) {
    const provider = getCanvasCaptureProvider(cv); // 있으면 우선 사용
    const cr = cv.getBoundingClientRect();

    // el과 교집합만 캡처
    const ixL = Math.max(r.left, cr.left);
    const ixT = Math.max(r.top, cr.top);
    const ixR = Math.min(r.right, cr.right);
    const ixB = Math.min(r.bottom, cr.bottom);
    const ixW = Math.max(0, ixR - ixL);
    const ixH = Math.max(0, ixB - ixT);
    if (ixW <= 0 || ixH <= 0) continue;

    let src: CanvasImageSource | null = null;
    let srcW = 0,
      srcH = 0;

    if (provider) {
      try {
        const url = await provider();
        const img = new Image();
        img.src = url;
        await img.decode();
        src = img;
        srcW = img.naturalWidth;
        srcH = img.naturalHeight;
      } catch {
        /* 실패 시 아래 캔버스로 폴백 */
      }
    }
    if (!src) {
      src = cv;
      srcW = cv.width;
      srcH = cv.height;
    }

    const scaleX = srcW / Math.max(1, cr.width);
    const scaleY = srcH / Math.max(1, cr.height);

    const sx = Math.round((ixL - cr.left) * scaleX);
    const sy = Math.round((ixT - cr.top) * scaleY);
    const sw = Math.round(ixW * scaleX);
    const sh = Math.round(ixH * scaleY);

    const dx = Math.round((ixL - r.left) * dpr);
    const dy = Math.round((ixT - r.top) * dpr);
    const dw = Math.round(ixW * dpr);
    const dh = Math.round(ixH * dpr);

    if (sw > 0 && sh > 0 && dw > 0 && dh > 0) {
      ctx.drawImage(src, sx, sy, sw, sh, dx, dy, dw, dh);
    }
  }
}

function resolveOpaqueBackground(el: HTMLElement): string | null {
  // el → 부모로 올라가며 첫 번째 '완전 불투명' 배경색을 찾음
  let cur: HTMLElement | null = el;
  while (cur) {
    const bg = getComputedStyle(cur).backgroundColor; // e.g. rgba(0,0,0,0.6) or rgb(33,33,33)
    if (bg && bg !== 'transparent') {
      // alpha 파싱
      const m = bg.match(/rgba?\(([^)]+)\)/i);
      if (m) {
        const parts = m[1].split(',').map((s) => s.trim());
        const a = parts[3] !== undefined ? parseFloat(parts[3]) : 1;
        if (!Number.isNaN(a) && a >= 1) return bg; // 불투명
      } else {
        return bg; // rgb(...)거나 named color
      }
    }
    cur = cur.parentElement;
  }
  return null;
}

export async function captureView(
  source: ViewSource,
  opts: CaptureOptions = {},
): Promise<string> {
  const format = opts.format ?? 'image/png';
  const quality = opts.quality ?? 0.92;

  await new Promise((r) => requestAnimationFrame(r));

  switch (source.type) {
    case 'vtk': {
      const rw = source.renderWindow;
      if (!rw) throw new Error('VTK renderWindow is undefined');

      if (typeof rw.captureNextImage === 'function') {
        try {
          const p = rw.captureNextImage(format, {
            scale: opts.scale ?? 1,
          });
          rw.render?.();
          const url = await p;
          if (!url) throw new Error('VTK captureNextImage returned empty');
          return url;
        } catch {}
      }

      if (typeof rw.captureImages === 'function') {
        const arr = rw.captureImages(format, {
          scale: opts.scale ?? 1,
        });
        if (arr?.[0]) return arr[0];
      }

      const view = rw.getViews?.()[0];
      if (view?.captureNextImage) {
        const p = view.captureNextImage(format, {
          scale: opts.scale ?? 1,
        });
        rw.render?.();
        const url = await p;
        if (!url) throw new Error('VTK view.captureNextImage returned empty');
        return url;
      }

      throw new Error('No compatible VTK capture API found');
    }

    case 'canvas': {
      const { canvas } = source;
      if (!canvas) throw new Error('Canvas is undefined');
      return canvas.toDataURL(format, quality);
    }

    case 'element': {
      const { element, rasterize } = source;
      if (!element) throw new Error('Element is undefined');
      if (typeof rasterize !== 'function') {
        throw new Error('Provide a rasterize(el) function for element capture');
      }
      const canvas = await rasterize(element);
      if (!canvas) throw new Error('Rasterize returned no canvas');
      return canvas.toDataURL(format, quality);
    }
  }
}


function svgToImageDataUrl(svg: SVGSVGElement): string {
  const serialized = new XMLSerializer().serializeToString(svg);
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(serialized)}`;
}

function getVisibleRect(element: Element): DOMRect | null {
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return null;
  const style = window.getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden') return null;
  return rect;
}

function rectIntersectionArea(a: DOMRect, b: DOMRect) {
  const left = Math.max(a.left, b.left);
  const top = Math.max(a.top, b.top);
  const right = Math.min(a.right, b.right);
  const bottom = Math.min(a.bottom, b.bottom);
  return Math.max(0, right - left) * Math.max(0, bottom - top);
}

function findSvgOverlayForContainer(
  container: HTMLElement,
  selector = 'svg#text-svg',
): SVGSVGElement | null {
  const direct = container.querySelector(selector) as SVGSVGElement | null;
  if (direct && getVisibleRect(direct)) return direct;

  const candidates = new Set<SVGSVGElement>();
  let node: HTMLElement | null = container;
  for (let depth = 0; node && depth < 4; depth += 1) {
    node.querySelectorAll(selector).forEach((item) => {
      candidates.add(item as SVGSVGElement);
    });
    node = node.parentElement;
  }

  // Some renderers mount SVG text as a sibling of the VTK container. If the
  // local ancestor walk misses it, use a document-level search and choose the
  // visible SVG that overlaps the VTK container the most.
  if (!candidates.size) {
    document.querySelectorAll(selector).forEach((item) => {
      candidates.add(item as SVGSVGElement);
    });
  }

  const containerRect = container.getBoundingClientRect();
  let best: SVGSVGElement | null = null;
  let bestArea = 0;
  candidates.forEach((candidate) => {
    const rect = getVisibleRect(candidate);
    if (!rect) return;
    const area = rectIntersectionArea(containerRect, rect);
    if (area > bestArea) {
      best = candidate;
      bestArea = area;
    }
  });

  return best;
}

function parseSvgNumber(value: string | null, fallback = 0) {
  if (value == null || value === '') return fallback;
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
}

function parseSvgOpacity(value: string | null, fallback = 1) {
  const num = parseSvgNumber(value, fallback);
  return Math.max(0, Math.min(1, num));
}

function getSvgElementColor(
  element: SVGElement,
  attr: 'fill' | 'stroke',
  fallback: string,
) {
  const attrValue = element.getAttribute(attr);
  if (attrValue && attrValue !== 'none') return attrValue;
  const styleValue = window.getComputedStyle(element)[attr];
  if (styleValue && styleValue !== 'none') return styleValue;
  return fallback;
}

function drawRoundedRectOnCanvas(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) {
  const r = Math.max(0, Math.min(radius, width / 2, height / 2));
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function drawSvgOverlayManually(
  ctx: CanvasRenderingContext2D,
  svg: SVGSVGElement,
  containerRect: DOMRect,
  scaleX: number,
  scaleY: number,
) {
  const svgRect = svg.getBoundingClientRect();
  const offsetX = (svgRect.left - containerRect.left) * scaleX;
  const offsetY = (svgRect.top - containerRect.top) * scaleY;
  const sx = (value: number) => offsetX + value * scaleX;
  const sy = (value: number) => offsetY + value * scaleY;

  svg.querySelectorAll('line, rect, text').forEach((node) => {
    const element = node as SVGElement;
    ctx.save();

    if (element.tagName.toLowerCase() === 'line') {
      const line = element as SVGLineElement;
      const x1 = parseSvgNumber(line.getAttribute('x1'));
      const y1 = parseSvgNumber(line.getAttribute('y1'));
      const x2 = parseSvgNumber(line.getAttribute('x2'));
      const y2 = parseSvgNumber(line.getAttribute('y2'));
      ctx.strokeStyle = getSvgElementColor(line, 'stroke', '#ffffff');
      ctx.globalAlpha = parseSvgOpacity(line.getAttribute('stroke-opacity'));
      ctx.lineWidth = Math.max(
        1,
        parseSvgNumber(line.getAttribute('stroke-width'), 1) * Math.max(scaleX, scaleY),
      );
      ctx.beginPath();
      ctx.moveTo(sx(x1), sy(y1));
      ctx.lineTo(sx(x2), sy(y2));
      ctx.stroke();
    } else if (element.tagName.toLowerCase() === 'rect') {
      const rect = element as SVGRectElement;
      const x = parseSvgNumber(rect.getAttribute('x'));
      const y = parseSvgNumber(rect.getAttribute('y'));
      const width = parseSvgNumber(rect.getAttribute('width'));
      const height = parseSvgNumber(rect.getAttribute('height'));
      const rx = parseSvgNumber(rect.getAttribute('rx'));
      const fill = getSvgElementColor(rect, 'fill', 'rgba(0, 0, 0, 0.5)');
      const stroke = rect.getAttribute('stroke');
      drawRoundedRectOnCanvas(
        ctx,
        sx(x),
        sy(y),
        width * scaleX,
        height * scaleY,
        rx * Math.max(scaleX, scaleY),
      );
      if (fill && fill !== 'none') {
        ctx.globalAlpha = parseSvgOpacity(rect.getAttribute('fill-opacity'));
        ctx.fillStyle = fill;
        ctx.fill();
      }
      if (stroke && stroke !== 'none') {
        ctx.globalAlpha = parseSvgOpacity(rect.getAttribute('stroke-opacity'));
        ctx.strokeStyle = stroke;
        ctx.lineWidth = Math.max(
          1,
          parseSvgNumber(rect.getAttribute('stroke-width'), 1) * Math.max(scaleX, scaleY),
        );
        ctx.stroke();
      }
    } else if (element.tagName.toLowerCase() === 'text') {
      const text = element as SVGTextElement;
      const x = parseSvgNumber(text.getAttribute('x'));
      const y = parseSvgNumber(text.getAttribute('y'));
      const computed = window.getComputedStyle(text);
      const fontSize = parseSvgNumber(
        text.getAttribute('font-size') || computed.fontSize,
        16,
      );
      const fontFamily = text.getAttribute('font-family') || computed.fontFamily || 'Arial';
      const fontWeight = text.getAttribute('font-weight') || computed.fontWeight || 'normal';
      const textAnchor = text.getAttribute('text-anchor') || computed.textAnchor || 'start';
      ctx.globalAlpha = parseSvgOpacity(text.getAttribute('fill-opacity'));
      ctx.fillStyle = getSvgElementColor(text, 'fill', '#ffffff');
      ctx.font = `${fontWeight} ${fontSize * Math.max(scaleX, scaleY)}px ${fontFamily}`;
      ctx.textAlign = textAnchor === 'middle' ? 'center' : textAnchor === 'end' ? 'right' : 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(text.textContent ?? '', sx(x), sy(y));
    }

    ctx.restore();
  });
}

export async function composeBaseImageWithSvgOverlay(
  baseImageUrl: string,
  container: HTMLElement,
  svgSelector = 'svg#text-svg',
): Promise<string> {
  const svg = findSvgOverlayForContainer(container, svgSelector);
  if (!svg) return baseImageUrl;

  const baseImage = await loadImageFlexible(baseImageUrl);
  const containerRect = container.getBoundingClientRect();
  const svgRect = svg.getBoundingClientRect();
  if (
    !baseImage.naturalWidth ||
    !baseImage.naturalHeight ||
    containerRect.width <= 0 ||
    containerRect.height <= 0 ||
    svgRect.width <= 0 ||
    svgRect.height <= 0
  ) {
    return baseImageUrl;
  }

  const out = document.createElement('canvas');
  out.width = baseImage.naturalWidth;
  out.height = baseImage.naturalHeight;
  const ctx = out.getContext('2d');
  if (!ctx) return baseImageUrl;

  ctx.drawImage(baseImage, 0, 0, out.width, out.height);

  const scaleX = out.width / containerRect.width;
  const scaleY = out.height / containerRect.height;

  // Prefer manual drawing because the FFR labels are simple SVG primitives and
  // this avoids browser differences when drawing serialized SVG data URLs.
  try {
    drawSvgOverlayManually(ctx, svg, containerRect, scaleX, scaleY);
    return out.toDataURL('image/png');
  } catch (manualError) {
    console.warn('[captureSurface3D][FFR] SVG manual overlay failed.', manualError);
  }

  try {
    const svgClone = svg.cloneNode(true) as SVGSVGElement;
    svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    svgClone.setAttribute('width', `${svgRect.width}`);
    svgClone.setAttribute('height', `${svgRect.height}`);
    svgClone.setAttribute('viewBox', `0 0 ${svgRect.width} ${svgRect.height}`);

    const overlayImage = await loadImageFlexible(svgToImageDataUrl(svgClone));
    const dx = (svgRect.left - containerRect.left) * scaleX;
    const dy = (svgRect.top - containerRect.top) * scaleY;
    const dw = svgRect.width * scaleX;
    const dh = svgRect.height * scaleY;
    ctx.drawImage(overlayImage, dx, dy, dw, dh);
    return out.toDataURL('image/png');
  } catch (svgImageError) {
    console.warn('[captureSurface3D][FFR] SVG image overlay failed.', svgImageError);
    return baseImageUrl;
  }
}

async function loadImageFlexible(src: string): Promise<HTMLImageElement> {
  const isDataURL = src.startsWith('data:');
  const img = new Image();
  if (!isDataURL) img.crossOrigin = 'anonymous';
  img.src = src;
  try {
    if ('decode' in img) {
      // @ts-ignore
      await img.decode();
    } else {
      await new Promise<void>((res, rej) => {
        (img as HTMLImageElement).onload = () => res();
        (img as HTMLImageElement).onerror = rej;
      });
    }
    return img;
  } catch {
    if (isDataURL) throw new Error('Failed to decode dataURL image.');
    const resp = await fetch(src);
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    try {
      const img2 = new Image();
      img2.src = url;
      if ('decode' in img2) {
        // @ts-ignore
        await img2.decode();
      } else {
        await new Promise<void>((res, rej) => {
          (img2 as HTMLImageElement).onload = () => res();
          (img2 as HTMLImageElement).onerror = rej;
        });
      }
      return img2;
    } finally {
      URL.revokeObjectURL(url);
    }
  }
}

function canvasToPNGBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('toBlob failed'))),
      'image/png',
    );
  });
}

function triggerDownload(blob: Blob, filename: string) {
  const objectURL = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectURL;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectURL);
}

export async function exportURLsAsPNGs(
  urls: string[],
  baseName = 'capture',
  startIndex = 1,
  delayMs = 60,
) {
  await new Promise((r) => requestAnimationFrame(r));

  for (let i = 0; i < urls.length; i++) {
    const img = await loadImageFlexible(urls[i]);

    const c = document.createElement('canvas');
    c.width = img.naturalWidth || img.width;
    c.height = img.naturalHeight || img.height;
    c.getContext('2d')!.drawImage(img, 0, 0);

    const blob = await canvasToPNGBlob(c);
    const filename = `${baseName}${startIndex + i}.png`;
    triggerDownload(blob, filename);

    if (delayMs > 0 && i < urls.length - 1) {
      await new Promise((r) => setTimeout(r, delayMs));
    }
  }
}

// export async function imageUrlToArrayBufferSimple(url: string): Promise<ArrayBuffer> {
//   const res = await fetch(url);
//   if (!res.ok) throw new Error(`Failed: ${res.status} ${res.statusText}`);
//   return res.arrayBuffer();
// }

export interface BufferData {
  buffer: ArrayBuffer;
  width: number;
  height: number;
  extension: string;
  orientation?: number[];
  position?: number[];
  cprinfo?: CPRInfo;
  branch_name?: string;
}

export async function imageUrlToArrayBufferSimple(
  url: string,
): Promise<BufferData> {
  // 1) 가져오기
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed: ${res.status} ${res.statusText}`);

  const contentType = res.headers.get('content-type') ?? undefined;
  const blob = await res.blob();

  // 2) 크기 구하기 (createImageBitmap -> fallback: HTMLImageElement)
  let width: number;
  let height: number;

  if (typeof createImageBitmap === 'function') {
    // 대부분의 최신 브라우저 & Electron 렌더러에서 지원
    const bmp = await createImageBitmap(blob);
    width = bmp.width;
    height = bmp.height;
    // 리소스 정리
    if ('close' in bmp) (bmp as ImageBitmap).close();
  } else {
    // 구형 브라우저/특수 환경 fallback
    const objectUrl = URL.createObjectURL(blob);
    try {
      const img = new Image();
      img.src = objectUrl;

      // decode()가 있다면 로딩 대기 (Safari 등 호환 위해 onload도 함께 처리)
      await (img.decode?.() ??
        new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = (e) => reject(e);
        }));

      width = img.naturalWidth;
      height = img.naturalHeight;
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }

  // 3) 바이트 버퍼 반환
  const buffer = await blob.arrayBuffer();
  return { buffer, width, height, extension: 'png' };
}

export async function angleCPRCapture(
  context: React.RefObject<CPRContext>,
  angle: number,
  count: number,
): Promise<[string[], number, number, number[]]> {
  if (!context.current) return [[], 0, 0, []];
  const ori_angle = context.current.angle;
  const frames: {
    url: string;
    width: number;
    height: number;
    midY: number;
  }[] = [];
  let maxwidth = 0;
  let angle_arr: number[] = [];
  for (let i = 0; i < count; i++) {
    const change_angle = ori_angle + angle * i;
    angle_arr.push(change_angle);
    updateCPRAngleAndPath2(context, change_angle);
    const { renderWindow, actorIds } = context.current!;
    const actor = actorIds['CPRActor'] as vtkImageSlice;
    const mapper = actor.getMapper() as unknown as vtkImageCPRMapper;
    let url = await captureView({
      type: 'vtk',
      renderWindow: context.current!.renderWindow,
    });
    const img = await loadImage(url);
    const centerPoint = mapper.getCenterPoint?.();
    const orientedCenterline = mapper.getOrientedCenterline?.();
    const points = orientedCenterline?.getPoints?.()?.getData?.() ?? null;
    const distances = orientedCenterline?.getDistancesToFirstPoint?.() ?? [];
    let midIndex = Math.floor(points?.length ? points.length / 6 : 0);
    if (centerPoint && points && points.length >= 3) {
      let bestIndex = 0;
      let bestDist = Infinity;
      const cx = centerPoint[0];
      const cy = centerPoint[1];
      const cz = centerPoint[2];
      for (let j = 0; j < points.length; j += 3) {
        const dx = points[j] - cx;
        const dy = points[j + 1] - cy;
        const dz = points[j + 2] - cz;
        const d2 = dx * dx + dy * dy + dz * dz;
        if (d2 < bestDist) {
          bestDist = d2;
          bestIndex = j / 3;
        }
      }
      midIndex = bestIndex;
    }

    const heightMM = mapper.getHeight?.() ?? 0;
    const distanceAtIndex =
      distances && distances.length > midIndex
        ? distances[midIndex]
        : heightMM / 2;
    const midRatio = heightMM > 0 ? distanceAtIndex / heightMM : 0.5;

    const trimmed = await trimMarginsToContentWithOffsets(url);
    // RGB 캡처 정렬 안정화: trim 결과 높이 기준 비율 매핑 유지
    const midYTrimmed = trimmed.height * midRatio;
    frames.push({
      url: trimmed.url,
      width: trimmed.width,
      height: trimmed.height,
      midY: midYTrimmed,
    });
    maxwidth = Math.max(maxwidth, trimmed.width);
  }

  const targetHeight = computeTargetHeight(frames);
  const targetWidth = Math.max(1, maxwidth);
  const urls = await Promise.all(
    frames.map((frame) => {
      const padLeft = clamp(
        Math.round((targetWidth - frame.width) / 2),
        0,
        Math.max(0, targetWidth - frame.width),
      );
      const padTop = clamp(
        Math.round(targetHeight / 2 - frame.midY),
        0,
        Math.max(0, targetHeight - frame.height),
      );
      return padImageToSize(
        frame.url,
        targetWidth,
        targetHeight,
        padLeft,
        padTop,
      );
    }),
  );

  return [urls, targetWidth, targetHeight, angle_arr];
}
export async function angleCPRISRCapture(
  context: React.RefObject<CPRContext>,
  index: number[],
  angle: number,
) {
  const { renderWindow } = context.current!;
  const src = renderWindow.captureImages();
  const urlPromise = Array.isArray(src) ? src[0] : src;
  const urlSrent = await urlPromise;
  const img = await loadImage(urlSrent);
  const w = img.width;
  const h = img.height;

  const { startX, startY } = getPerpendicularLine(
    index[0],
    w / 4,
    context.current!.path,
  );
  const { endX, endY } = getPerpendicularLine(
    index[1],
    w / 4,
    context.current!.path,
  );

  const rad = (angle * Math.PI) / 180;
  const cos = Math.abs(Math.cos(rad));
  const sin = Math.abs(Math.sin(rad));
  const outW = Math.ceil(w * cos + h * sin);
  const outH = Math.ceil(w * sin + h * cos);

  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D context not available.');

  ctx.translate(outW / 2, outH / 2);
  ctx.rotate(rad);

  let DPR = 1;
  try {
    const view = renderWindow.getViews?.()[0];
    const canvasEl: HTMLCanvasElement | undefined =
      view?.getCanvas?.() ??
      (view?.getRenderable?.() as HTMLCanvasElement | undefined);
    const cssW = canvasEl?.clientWidth || img.width;
    DPR = img.width / cssW;
  } catch {
    DPR = window.devicePixelRatio || 1;
  }

  const cropWidth = Math.floor(Math.abs(endX - startX) * DPR);
  const cropHeight = Math.floor(Math.abs(endY - startY) * DPR);
  const cropX = Math.floor(Math.min(startX, endX) * DPR);
  const cropY = Math.floor(Math.min(startY, endY) * DPR);

  // 회전된 좌표계 기준으로 clip path 설정
  ctx.beginPath();
  ctx.rect(cropX - w / 2, cropY - h / 2, cropWidth, cropHeight);
  ctx.clip();
  ctx.drawImage(img, -w / 2, -h / 2);
  const rotated = canvas.toDataURL('image/png');
  const [trimmed, cw, ch] = await trimMarginsToContent(rotated);
  const fullImage = (await captureCanvaswithNotBlank(
    context.current!.renderWindow.captureImages(),
    angle,
  )) as string;
  return {
    stent: trimmed,
    full: fullImage,
  };
}

export async function captureCanvaswithNotBlank(
  src: Promise<string> | Promise<string>[],
  rotate: number = 0,
): Promise<string> {
  const urlPromise = Array.isArray(src) ? src[0] : src;
  const url = await urlPromise;
  const img = await loadImage(url);

  const w = img.width;
  const h = img.height;
  const rad = (rotate * Math.PI) / 180;
  const cos = Math.abs(Math.cos(rad));
  const sin = Math.abs(Math.sin(rad));
  const outW = Math.ceil(w * cos + h * sin);
  const outH = Math.ceil(w * sin + h * cos);

  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D context not available.');
  ctx.translate(outW / 2, outH / 2);
  ctx.rotate(rad);
  ctx.drawImage(img, -w / 2, -h / 2);

  const rotated = canvas.toDataURL('image/png');
  const [trimmed, cw, ch] = await trimMarginsToContent(rotated);
  return trimmed;
}

async function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  const image = new Image();
  image.src = dataUrl;

  if ('decode' in image) {
    try {
      await image.decode();
      return image;
    } catch {}
  }

  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = (e) => reject(e);
  });

  return image;
}

export async function cropToSquare(
  src: Promise<string> | string,
): Promise<string> {
  const url = typeof src === 'string' ? src : await src;
  const img = await loadImage(url);

  const w = img.naturalWidth || (img as any).width;
  const h = img.naturalHeight || (img as any).height;
  if (!w || !h) throw new Error('Invalid image size');

  const size = Math.min(w, h);
  const sx = Math.floor((w - size) / 2);
  const sy = Math.floor((h - size) / 2);

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D context not available');

  ctx.drawImage(img, sx, sy, size, size, 0, 0, size, size);
  return canvas.toDataURL('image/png');
}

export async function trimMarginsToContent(
  src: Promise<string> | string,
): Promise<[string, number, number]> {
  const TOL = 12;
  const BG_RATIO = 0.995;
  const ALPHA_TH = 8;
  const SAMPLE_STEP = 2;

  const url = typeof src === 'string' ? src : await src;
  const img = await loadImage(url);

  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D context not available');
  ctx.drawImage(img, 0, 0);

  const w = canvas.width,
    h = canvas.height;
  const { data } = ctx.getImageData(0, 0, w, h);

  function getRGB(
    data: Uint8ClampedArray,
    w: number,
    x: number,
    y: number,
  ): [number, number, number] {
    const i = (y * w + x) * 4;
    return [data[i], data[i + 1], data[i + 2]];
  }

  const corners: [number, number, number][] = [
    getRGB(data, w, 0, 0),
    getRGB(data, w, w - 1, 0),
    getRGB(data, w, 0, h - 1),
    getRGB(data, w, w - 1, h - 1),
  ];

  const isBgPixel = (i: number) => {
    const a = data[i + 3];
    if (a <= ALPHA_TH) return true;
    const r = data[i],
      g = data[i + 1],
      b = data[i + 2];
    for (const [cr, cg, cb] of corners) {
      if (
        Math.abs(r - cr) <= TOL &&
        Math.abs(g - cg) <= TOL &&
        Math.abs(b - cb) <= TOL
      )
        return true;
    }
    return false;
  };

  const rowIsBg = (y: number) => {
    let bg = 0,
      tot = 0;
    for (let x = 0; x < w; x += SAMPLE_STEP) {
      const i = (y * w + x) * 4;
      if (isBgPixel(i)) bg++;
      tot++;
    }
    return tot > 0 && bg / tot >= BG_RATIO;
  };

  const colIsBg = (x: number, top: number, bottom: number) => {
    let bg = 0,
      tot = 0;
    for (let y = top; y <= bottom; y += SAMPLE_STEP) {
      const i = (y * w + x) * 4;
      if (isBgPixel(i)) bg++;
      tot++;
    }
    return tot > 0 && bg / tot >= BG_RATIO;
  };

  let top = 0,
    bottom = h - 1,
    left = 0,
    right = w - 1;

  while (top <= bottom && rowIsBg(top)) top++;
  while (bottom >= top && rowIsBg(bottom)) bottom--;
  while (left <= right && colIsBg(left, top, bottom)) left++;
  while (right >= left && colIsBg(right, top, bottom)) right--;

  if (top === 0 && bottom === h - 1 && left === 0 && right === w - 1) {
    return [canvas.toDataURL('image/png'), w, h];
  }

  const cw = right - left + 1;
  const ch = bottom - top + 1;
  const out = document.createElement('canvas');
  out.width = cw;
  out.height = ch;
  const octx = out.getContext('2d');
  if (!octx) throw new Error('2D context not available');
  octx.drawImage(canvas, left, top, cw, ch, 0, 0, cw, ch);
  return [out.toDataURL('image/png'), cw, ch];
}

export async function trimMarginsToContentWithOffsets(
  src: Promise<string> | string,
): Promise<{
  url: string;
  width: number;
  height: number;
  left: number;
  top: number;
  originalWidth: number;
  originalHeight: number;
}> {
  const url = typeof src === 'string' ? src : await src;
  const img = await loadImage(url);

  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D context not available');
  ctx.drawImage(img, 0, 0);

  const w = canvas.width,
    h = canvas.height;
  const { data } = ctx.getImageData(0, 0, w, h);

  const TOL = 12;
  const BG_RATIO = 0.995;
  const ALPHA_TH = 8;
  const SAMPLE_STEP = 2;

  function getRGB(
    data: Uint8ClampedArray,
    w: number,
    x: number,
    y: number,
  ): [number, number, number] {
    const i = (y * w + x) * 4;
    return [data[i], data[i + 1], data[i + 2]];
  }

  const corners: [number, number, number][] = [
    getRGB(data, w, 0, 0),
    getRGB(data, w, w - 1, 0),
    getRGB(data, w, 0, h - 1),
    getRGB(data, w, w - 1, h - 1),
  ];

  const isBgPixel = (i: number) => {
    const a = data[i + 3];
    if (a <= ALPHA_TH) return true;
    const r = data[i],
      g = data[i + 1],
      b = data[i + 2];
    for (const [cr, cg, cb] of corners) {
      if (
        Math.abs(r - cr) <= TOL &&
        Math.abs(g - cg) <= TOL &&
        Math.abs(b - cb) <= TOL
      )
        return true;
    }
    return false;
  };

  const rowIsBg = (y: number) => {
    let bg = 0,
      tot = 0;
    for (let x = 0; x < w; x += SAMPLE_STEP) {
      const i = (y * w + x) * 4;
      if (isBgPixel(i)) bg++;
      tot++;
    }
    return tot > 0 && bg / tot >= BG_RATIO;
  };

  const colIsBg = (x: number, top: number, bottom: number) => {
    let bg = 0,
      tot = 0;
    for (let y = top; y <= bottom; y += SAMPLE_STEP) {
      const i = (y * w + x) * 4;
      if (isBgPixel(i)) bg++;
      tot++;
    }
    return tot > 0 && bg / tot >= BG_RATIO;
  };

  let top = 0,
    bottom = h - 1,
    left = 0,
    right = w - 1;

  while (top <= bottom && rowIsBg(top)) top++;
  while (bottom >= top && rowIsBg(bottom)) bottom--;
  while (left <= right && colIsBg(left, top, bottom)) left++;
  while (right >= left && colIsBg(right, top, bottom)) right--;

  if (top === 0 && bottom === h - 1 && left === 0 && right === w - 1) {
    return {
      url: canvas.toDataURL('image/png'),
      width: w,
      height: h,
      left: 0,
      top: 0,
      originalWidth: w,
      originalHeight: h,
    };
  }

  const cw = right - left + 1;
  const ch = bottom - top + 1;
  const out = document.createElement('canvas');
  out.width = cw;
  out.height = ch;
  const octx = out.getContext('2d');
  if (!octx) throw new Error('2D context not available');
  octx.drawImage(canvas, left, top, cw, ch, 0, 0, cw, ch);
  return {
    url: out.toDataURL('image/png'),
    width: cw,
    height: ch,
    left,
    top,
    originalWidth: w,
    originalHeight: h,
  };
}

async function padImageToSize(
  src: string,
  targetWidth: number,
  targetHeight: number,
  padLeft: number,
  padTop: number,
): Promise<string> {
  const img = await loadImage(src);
  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D context not available');
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, targetWidth, targetHeight);
  ctx.drawImage(img, padLeft, padTop);
  return canvas.toDataURL('image/png');
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function computeTargetSize(
  items: { width: number; height: number; midX: number; midY: number }[],
): { width: number; height: number } {
  if (!items.length) return { width: 0, height: 0 };
  let maxHalfX = 0;
  let maxHalfY = 0;
  for (const item of items) {
    const midX = Number.isFinite(item.midX) ? item.midX : item.width / 2;
    const midY = Number.isFinite(item.midY) ? item.midY : item.height / 2;
    maxHalfX = Math.max(maxHalfX, midX, item.width - midX);
    maxHalfY = Math.max(maxHalfY, midY, item.height - midY);
  }
  return {
    width: Math.max(1, Math.ceil(maxHalfX * 2)),
    height: Math.max(1, Math.ceil(maxHalfY * 2)),
  };
}

function computeTargetHeight(
  items: { height: number; midY: number }[],
): number {
  if (!items.length) return 0;
  let maxHalf = 0;
  for (const item of items) {
    const mid = Number.isFinite(item.midY) ? item.midY : item.height / 2;
    maxHalf = Math.max(maxHalf, mid, item.height - mid);
  }
  return Math.max(1, Math.ceil(maxHalf * 2));
}

function padCPRHUResult(
  result: CPRExportResult,
  midY: number,
  targetHeight: number,
): CPRExportResult {
  const width = result.width;
  const height = result.height;
  const padTop = clamp(
    Math.round(targetHeight / 2 - midY),
    0,
    Math.max(0, targetHeight - height),
  );
  const out = new Int16Array(targetHeight * width).fill(
    Math.round(result.extent.minHU),
  );
  for (let y = 0; y < height; y++) {
    const srcRow = y * width;
    const dstRow = (y + padTop) * width;
    out.set(result.data.subarray(srcRow, srcRow + width), dstRow);
  }
  return {
    ...result,
    height: targetHeight,
    data: out,
  };
}

function createCPRTemp(
  context: React.MutableRefObject<CPRContext | null>,
  imageData: vtkImageData,
  // seriesId: string,
  labelData: vtkImageData,
  centerlineData: vtkPolyData,
  mode: 'stretched' | 'straightened',
  analysisType: AnalysisType,
  currentVOI: VOI,
  colorPoints?: Array<[number, number, number, number]>,
  opacityPoints?: Array<[number, number]>,
  stretchedWidth?: number,
) {
  if (!context.current) return;
  const { renderer, actorIds, angle, axis } = context.current;
  if (!renderer || !imageData) return;
  renderer.removeAllActors();
  let new_axis = axis!;

  const maxindex = centerlineData.getPoints().getNumberOfPoints();
  const startpoint = centerlineData
    .getPoints()
    .getPoint(configStore.get().coronary_front_expand);
  const endpoint = centerlineData
    .getPoints()
    .getPoint(maxindex - 1 - configStore.get().coronary_back_expand);
  vec3.sub(
    new_axis,
    [endpoint[0], endpoint[1], endpoint[2]],
    [startpoint[0], startpoint[1], startpoint[2]],
  );
  vec3.normalize(new_axis, new_axis);
  if (mode === 'straightened') new_axis = [0, 0, 1];
  new_axis = [0, 0, 1];
  const width_size =
    mode === 'stretched'
      ? stretchedWidth === undefined
        ? 300
        : stretchedWidth
      : analysisType === 'aorticValveCalcium'
        ? 150
        : 100;

  const { mapper: CPRMapper, actor: CPRActor } = createCPRActorAndMapper({
    data: imageData,
    mode,
    width: width_size,
    centerline: centerlineData,
    currentVOI,
  });

  const { mapper: CPRPaintMapper, actor: CPRPaintActor } =
    createCPRActorAndMapper({
      mode: mode,
      data: labelData,
      width: width_size,
      centerline: centerlineData,
      currentVOI,
      isPaintData: true,
      colorPoints,
      opacityPoints,
    });

  CPRPaintActor.getProperty().setPiecewiseFunction(
    0,
    (actorIds['CPRPaintActor'] as vtkImageSlice)
      .getProperty()
      .getPiecewiseFunction(),
  );
  CPRPaintActor.setVisibility(actorIds['CPRPaintActor'].getVisibility());
  renderer.addActor(CPRActor);
  renderer.addActor(CPRPaintActor);
  actorIds['CPRActor'] = CPRActor;
  actorIds['CPRPaintActor'] = CPRPaintActor;

  renderer.resetCamera();
  renderer.getActiveCamera().setParallelProjection(true);
  const pointsArr = centerlineData.getPoints().getData();
  const maxheight = pointsArr.length / 3;

  const camera = renderer.getActiveCamera();

  context.current['labelData'] = labelData;
  context.current['imageData'] = imageData;
  context.current['centerlineData'] = centerlineData;
  context.current['axis'] = new_axis;
  context.current['mode'] = mode;
  context.current['width'] = width_size;
  context.current['height'] = maxheight;
  context.current['spacing'] = imageData.getSpacing();
  context.current['zoom'] = camera.getParallelScale();
  context.current['markerline_visibility'] = true;
  context.current['colorPoints'] = colorPoints;
  context.current['opacityPoints'] = opacityPoints;

  updateCPRAngleAndPath2(context, angle);
}

export async function updateCPRAngleAndPath2(
  context: React.MutableRefObject<CPRContext | null>,
  updatedAngle: number,
) {
  if (!context.current) return;
  const { savedTranslation, axis, actorIds, renderer, canvas } =
    context.current;

  const radAngle = radiansFromDegrees(updatedAngle);

  handleCPRAngle(context, radAngle, savedTranslation, axis!);
}

export const nextFrame = () =>
  new Promise<void>((r) => requestAnimationFrame(() => r()));

export function setNewContainer(
  genericRenderWindow: vtkGenericRenderWindow,
  width: number,
  height: number,
) {
  const host = document.createElement('div');
  host.style.position = 'fixed';
  host.style.left = '0px';
  host.style.top = '0px';
  host.style.width = `${width}px`;
  host.style.height = `${height}px`;
  host.style.pointerEvents = 'none';
  host.style.visibility = 'hidden';
  document.body.appendChild(host);

  if (host && genericRenderWindow) {
    genericRenderWindow.setContainer(host);
    genericRenderWindow.resize();
  }
}
// 기존의 context 사용해서 캡쳐하는 함수
//여기서 createCPR을 하기에, image,label바꿔쓰면 돌려놓는걸 따로 해야함.
export async function captureCPROffscreen(
  context: React.MutableRefObject<CPRContext | null>,
  imageData: vtkImageData,
  labelData: vtkImageData,
  centerData: vtkPolyData,
  width: number,
  height: number,
  angle: number,
  count: number,
  analysisType: AnalysisType,
  currentVOI: VOI,
  capture: boolean = true,
  prevImageData?: vtkImageData,
  prevLabelData?: vtkImageData,
): Promise<[string[], number, number, number[]]> {
  let urls: string[] = [];
  let maxw: number = 0;
  let maxh: number = 0;
  let angles: number[] = [];

  if (!context.current) {
    return [urls, maxw, maxh, angles];
  }

  if (capture) {
    const { genericRenderWindow, renderer, renderWindow } = context.current;
    const ref = genericRenderWindow.getContainer();
    setNewContainer(genericRenderWindow, width, height);

    createCPRTemp(
      context,
      imageData,
      labelData,
      centerData,
      context.current.mode!,
      analysisType,
      currentVOI,
      context.current.colorPoints,
      context.current.opacityPoints,
      configStore.get().captureCPRWidth,
    );

    updatelabel(context.current, 'lumen', configStore.get().label.lumen);
    updatelabel(context.current, 'plaque', configStore.get().label.plaque);

    await nextFrame();

    renderer.resetCamera();
    renderWindow.render();
    renderWindow.modified();

    await nextFrame();
    const [us, mw, mh, angle_arr] = await angleCPRCapture(
      context,
      angle,
      count,
    );
    urls = us;
    maxw = mw;
    maxh = mh;
    angles = angle_arr;

    if (ref) genericRenderWindow.setContainer(ref);
  }

  //돌려놓기용
  if (prevImageData && prevLabelData) {
    createCPRTemp(
      context,
      prevImageData,
      prevLabelData,
      centerData,
      context.current.mode!,
      analysisType,
      currentVOI,
      context.current.colorPoints,
      context.current.opacityPoints,
    );
  } else {
    if (!capture) {
      createCPRTemp(
        context,
        imageData,
        labelData,
        centerData,
        context.current.mode!,
        analysisType,
        currentVOI,
        context.current.colorPoints,
        context.current.opacityPoints,
      );
    }
  }
  resetCPRView(context);
  return [urls, maxw, maxh, angles];
}

type CPRCaptureMode = 'stretched' | 'straightened';
const CPR_CAPTURE_MODE_ORDER: CPRCaptureMode[] = ['stretched', 'straightened'];

function getCPRModeCaptureConfig(mode: CPRCaptureMode) {
  return mode === 'stretched'
    ? {
        mode,
        key: 'CPR.0.stretched' as const,
        angle: configStore.get().stretchedAngle,
        count: configStore.get().stretchedCount,
      }
    : {
        mode,
        key: 'CPR.0.straightened' as const,
        angle: configStore.get().straightenedAngle,
        count: configStore.get().straightenedCount,
      };
}

function buildCPRModeCaptureConfigs(modes?: CPRCaptureMode[]) {
  const targetModes = modes?.length ? modes : CPR_CAPTURE_MODE_ORDER;
  return targetModes.map(getCPRModeCaptureConfig);
}

export async function cprhucaptureByMode(
  poolRef: React.MutableRefObject<ContextPool>,
  pool: BufferPoolItem,
  captureWidth: number,
  captureHeight: number,
  analysisType: AnalysisType,
  currentVOI: VOI,
  isCapture: boolean = true,
  centerlineData?: vtkPolyData,
  modes?: CPRCaptureMode[],
) {
  const keys = buildCPRModeCaptureConfigs(modes);
  const datasByMode: Record<CPRCaptureMode, CPRExportResult[]> = {
    stretched: [],
    straightened: [],
  };
  const { renderingQuality } = configStore.get().generalSetting;
  const lowQ = renderingQuality !== 'high';

  for (const data of keys) {
    const { mode, key, angle, count } = data;
    const con = {
      current: poolRef.current[key as ContextKey] as CPRContext,
    } as React.MutableRefObject<CPRContext | null>;
    if (!con.current) continue;

    const prevImageData = con.current.imageData!;
    const prevLabelData = con.current.labelData!;

    const imageData = lowQ
      ? con.current.sharedContext.oriHUData!
      : prevImageData;
    // const labelData = lowQ?con.current.sharedContext.oriLabelData!:prevLabelData;

    if (isCapture) {
      const { genericRenderWindow, renderer, renderWindow } = con.current;
      const ref = genericRenderWindow.getContainer();
      setNewContainer(genericRenderWindow, captureWidth, captureHeight);

      createCPRTemp(
        con,
        imageData,
        prevLabelData,
        centerlineData ?? con.current?.centerlineData!,
        con.current.mode!,
        analysisType,
        currentVOI,
        con.current.colorPoints,
        con.current.opacityPoints,
        configStore.get().captureCPRWidth,
      );

      await nextFrame();

      renderer.resetCamera();
      renderWindow.render();
      renderWindow.modified();

      await nextFrame();

      async function hucap(cprcontext: CPRContext) {
        let hudatas: { result: CPRExportResult; midY: number }[] = [];
        const ori_angle = cprcontext.angle;
        const { imageData } = cprcontext;
        if (imageData) {
          for (let i = 0; i < count; i++) {
            const change_angle = ori_angle + angle * i;
            updateCPRAngleAndPath2(con, change_angle);
            const { actorIds, axis } = cprcontext;
            const CPRActor = actorIds['CPRActor'] as vtkImageSlice;
            const CPRMapper =
              CPRActor.getMapper() as unknown as vtkImageCPRMapper;

            const data = await exportCPRHU(CPRMapper, imageData, {
              pixelSpacingU: imageData.getSpacing()[0],
              pixelSpacingV: imageData.getSpacing()[2],
              projectionAxis: 'tangent',
              rotationAxisWorld: axis,
              angle: change_angle,
            });
            const centerline = CPRMapper.getOrientedCenterline?.();
            const distances = centerline?.getDistancesToFirstPoint?.() ?? [];
            const heightMM = CPRMapper.getHeight?.() ?? 0;
            const midDistance =
              distances.length > 0
                ? distances[Math.floor(distances.length / 2)]
                : heightMM / 2;
            const midY =
              heightMM > 0 ? (midDistance / heightMM) * data.height : 0;
            hudatas.push({ result: data, midY });
          }
        }
        const targetHeight = computeTargetHeight(
          hudatas.map((d) => ({ height: d.result.height, midY: d.midY })),
        );
        return hudatas.map((d) =>
          padCPRHUResult(d.result, d.midY, targetHeight),
        );
      }

      const curdata = await hucap(con.current);
      datasByMode[mode].push(...curdata);
      if (ref) genericRenderWindow.setContainer(ref);
    }

    if (lowQ) {
      createCPRTemp(
        con,
        prevImageData,
        prevLabelData,
        centerlineData ?? con.current?.centerlineData!,
        con.current.mode!,
        analysisType,
        currentVOI,
        con.current.colorPoints,
        con.current.opacityPoints,
      );
    } else {
      if (!isCapture) {
        createCPRTemp(
          con,
          imageData,
          prevLabelData,
          centerlineData ?? con.current?.centerlineData!,
          con.current.mode!,
          analysisType,
          currentVOI,
          con.current.colorPoints,
          con.current.opacityPoints,
        );
      }
    }
    resetCPRView(con);
  }
  return datasByMode;
}

export async function cprhucapture(
  poolRef: React.MutableRefObject<ContextPool>,
  pool: BufferPoolItem,
  captureWidth: number,
  captureHeight: number,
  analysisType: AnalysisType,
  currentVOI: VOI,
  isCapture: boolean = true,
  centerlineData?: vtkPolyData,
) {
  const byMode = await cprhucaptureByMode(
    poolRef,
    pool,
    captureWidth,
    captureHeight,
    analysisType,
    currentVOI,
    isCapture,
    centerlineData,
  );
  return CPR_CAPTURE_MODE_ORDER.flatMap((mode) => byMode[mode]);
}

export async function cprcaptureByMode({
  poolRef,
  pool,
  captureWidth,
  captureHeight,
  analysisType,
  currentVOI,
  isCapture,
  labelData,
  centerlineData,
  modes,
}: {
  poolRef: React.MutableRefObject<ContextPool>;
  pool: BufferPoolItem;
  captureWidth: number;
  captureHeight: number;
  analysisType: AnalysisType;
  currentVOI: VOI;
  isCapture: boolean;
  labelData: vtkImageData;
  centerlineData?: vtkPolyData;
  modes?: CPRCaptureMode[];
}) {
  const keys = buildCPRModeCaptureConfigs(modes);
  const datasByMode: Record<
    CPRCaptureMode,
    [string[], number, number, number[]][]
  > = {
    stretched: [],
    straightened: [],
  };
  const { renderingQuality } = configStore.get().generalSetting;
  const lowQ = renderingQuality !== 'high';

  for (const data of keys) {
    const { mode, key, angle, count } = data;
    const con = {
      current: poolRef.current[key as ContextKey] as CPRContext,
    } as React.MutableRefObject<CPRContext | null>;
    if (!con.current) continue;
    const prevImageData = con.current.imageData!;
    const prevLabelData = con.current.labelData!;

    const imageData = lowQ
      ? con.current.sharedContext.oriHUData!
      : prevImageData;
    // const labelData_ =  lowQ? labelData:prevLabelData;

    const [url, mw, mh, angles] = await captureCPROffscreen(
      con,
      imageData,
      labelData,
      centerlineData ?? con.current?.centerlineData!,
      captureWidth,
      captureHeight,
      angle,
      count,
      analysisType,
      currentVOI,
      isCapture,
      lowQ ? prevImageData : undefined,
      lowQ ? prevLabelData : undefined,
    );

    if (isCapture) {
      datasByMode[mode] = [...datasByMode[mode], [url, mw, mh, angles]];
    }
  }

  return datasByMode;
}

export async function cprcapture({
  poolRef,
  pool,
  captureWidth,
  captureHeight,
  analysisType,
  currentVOI,
  isCapture,
  labelData,
  centerlineData,
}: {
  poolRef: React.MutableRefObject<ContextPool>;
  pool: BufferPoolItem;
  captureWidth: number;
  captureHeight: number;
  analysisType: AnalysisType;
  currentVOI: VOI;
  isCapture: boolean;
  labelData: vtkImageData;
  centerlineData?: vtkPolyData;
}) {
  const byMode = await cprcaptureByMode({
    poolRef,
    pool,
    captureWidth,
    captureHeight,
    analysisType,
    currentVOI,
    isCapture,
    labelData,
    centerlineData,
  });
  return CPR_CAPTURE_MODE_ORDER.flatMap((mode) => byMode[mode]);
}

export async function captureSurface3D(
  context: SurfaceContext | VRContext,
  analysisType?: AnalysisType,
  pool?: BufferPoolItem,
): Promise<string | undefined> {
  const ctxRef = { current: context } as MutableRefObject<
    VRContext | SurfaceContext | null
  >;
  const {
    orientationWidget,
    renderer,
    renderWindow,
    genericRenderWindow,
    actorIds,
    viewportId,
  } = context;
  const isFFR = analysisType === 'FFR';
  const shouldResizeContainer = analysisType && !isFFR;
  const originalContainer = genericRenderWindow.getContainer();
  const originalAxisVisible = !!ctxRef.current?.isAxis3DVisible;
  let image: string | undefined;

  const captureVtkImage = async () =>
    captureView({
      type: 'vtk',
      renderWindow,
    });

  const getFfrCaptureElement = () => {
    const container = genericRenderWindow.getContainer() as HTMLDivElement | null;
    if (!container) return null;

    const panel = container.closest('.panel') as HTMLDivElement | null;
    return panel ?? container;
  };

  const captureFfrImage = async () => {
    const container = genericRenderWindow.getContainer() as HTMLDivElement | null;
    const captureElement = getFfrCaptureElement();

    await waitForNextPaint(2);

    // FFR capture should preserve exactly the user-visible 3D camera state
    // including pan/zoom and DOM overlays such as FFR labels and the FFR color
    // legend. Capture the panel DOM first, then fall back to VTK readback.
    if (captureElement) {
      try {
        return await screenshotElement(captureElement);
      } catch (domError) {
        console.warn(
          '[captureSurface3D][FFR] DOM capture failed; trying VTK fallback.',
          domError,
        );
      }
    }

    try {
      const vtkImage = await captureVtkImage();
      if (!container) return vtkImage;
      return await composeBaseImageWithSvgOverlay(vtkImage, container);
    } catch (vtkError) {
      console.warn('[captureSurface3D][FFR] VTK fallback capture failed.', vtkError);
      return undefined;
    }
  };

  try {
    setAxesLinesVisibility({ context: ctxRef, visible: false });
    orientationWidget?.setEnabled(false);

    if (shouldResizeContainer) {
      setNewContainer(
        genericRenderWindow,
        configStore.get().captureWidth,
        configStore.get().captureHeight,
      );
    }

    if (isFFR) {
      // Preserve the current user camera for FFR capture. The capture result
      // should match the visible pan/zoom/rotation state instead of fitting the
      // model back to center.
      renderer.resetCameraClippingRange();
    } else if (viewportId === 'VR' && analysisType) {
      updateResetCamera(ctxRef, analysisType, pool);
    } else if (analysisType) {
      const actorNames =
        analysisType === 'myocardial'
          ? ['aorta', 'coronary', 'lvWall']
          : [getActorNameByAnalysisType(analysisType)!];

      const bounds = mergeActorBounds(actorNames, actorIds);
      bounds ? renderer.resetCamera(bounds) : renderer.resetCamera();
    } else {
      renderer.resetCamera();
    }

    renderer.setBackground(0, 0, 0, 0);
    renderWindow.render();

    image = isFFR ? await captureFfrImage() : await captureVtkImage();
  } finally {
    if (shouldResizeContainer && originalContainer) {
      genericRenderWindow.setContainer(originalContainer);
    }

    orientationWidget?.setEnabled(true);
    setAxesLinesVisibility({
      context: ctxRef,
      visible: originalAxisVisible,
    });

    if (analysisType && !isFFR) {
      updateResetCamera(ctxRef, analysisType, pool);
    }

    genericRenderWindow.resize();
    renderWindow.render();
  }

  return image;
}

export async function captureDicom(
  viewportId: PanelComponentKeyType,
): Promise<string | undefined> {
  // const enabledElement = getEnabledElementByViewportId(viewportId);
  // const viewport = enabledElement.viewport;
  // const renderWindow = viewport?.getRenderer().getRenderWindow();
  const what = captureImage(viewportId, 'png');
  const [url, w, h] = await trimMarginsToContent(what!);
  return url;
}

export async function captureER(
  context: React.MutableRefObject<VRContext | null>,
  bufferPool: BufferPool,
  analysisType: AnalysisType,
  seriesId: string,
): Promise<string[]> {
  let urls: string[] = [];
  if (!context.current) {
    return urls;
  }
  const {
    renderer,
    renderWindow,
    actorIds,
    orientationWidget,
    isAxis3DVisible,
    efId,
  } = context.current;
  const backupActorIds = { ...actorIds };

  setAxesLinesVisibility({ context, visible: false });

  const camera: vtkCamera = renderer.getActiveCamera();
  const oriPosition = camera.getPosition();
  const oriViewUp = camera.getViewUp();
  const oriFocalPosition = camera.getFocalPoint();
  orientationWidget?.setEnabled(false);
  renderer.resetCamera();
  renderWindow.render();

  const keys = ['diastolic', 'systolic'];
  for (let i = 0; i < keys.length; i++) {
    //diastolic wall min chamber max
    const id = (
      keys[i] === 'diastolic'
        ? configStore.get().reportEF.maxChamberLV
        : configStore.get().reportEF.minChamberLV
    ) as string;
    updateEFVolumeMapper(
      context,
      bufferPool[seriesId],
      analysisType,
      seriesId!,
      id,
    );

    let url = await captureView({
      type: 'vtk',
      renderWindow: renderWindow,
    });
    const [u, w, h] = await trimMarginsToContent(url);
    url = u;
    urls.push(url);
    renderWindow.render();
  }

  if (efId)
    updateEFVolumeMapper(
      context,
      bufferPool[seriesId],
      analysisType,
      seriesId,
      efId,
    );

  camera.setPosition(...oriPosition);
  camera.setFocalPoint(...oriFocalPosition);
  camera.setViewUp(oriViewUp);
  renderer.resetCameraClippingRange();

  setAxesLinesVisibility({ context, visible: isAxis3DVisible });
  orientationWidget?.setEnabled(true);
  renderWindow.render();
  return urls;
}

export async function cprreportcapture(
  context: React.RefObject<CPRContext>,
  points: [number, number],
): Promise<[string, string]> {
  if (!context.current) return ['', ''];
  function getResolutionScaledLength(base = 50) {
    const DPR = window.devicePixelRatio || 1;
    const widthPx = Math.round(window.screen.width * DPR); // 물리 가로
    const heightPx = Math.round(window.screen.height * DPR); // 물리 세로

    if (widthPx === 1920 && heightPx === 1080) return base;

    const scaleW = widthPx / 1920;
    const scaleH = heightPx / 1080;
    const scale = Math.min(scaleW, scaleH);

    return Math.round(base * scale);
  }

  const lineLen = getResolutionScaledLength(50);
  const { renderWindow, renderer, path } = context.current;
  const { startX, startY } = getPerpendicularLine(points[0], lineLen, path);
  const { endX, endY } = getPerpendicularLine(points[1], lineLen, path);

  const [polyImagePromise] = renderWindow.captureImages();
  const polyImage = await polyImagePromise;

  const background = new Image();
  background.src = polyImage;

  await Promise.all([
    new Promise<void>((resolve) => {
      background.onload = () => resolve();
    }),
  ]);

  const originalCanvas = document.createElement('canvas');
  originalCanvas.width = background.width;
  originalCanvas.height = background.height;
  const originalCtx = originalCanvas.getContext('2d');
  if (!originalCtx) {
    console.error('2D 컨텍스트를 가져오지 못했습니다.');
    return ['', ''];
  }

  originalCtx.drawImage(background, 0, 0);

  const originalImage = originalCanvas.toDataURL('image/jpeg');

  let DPR = 1;
  try {
    const view = renderWindow.getViews?.()[0];
    const canvasEl: HTMLCanvasElement | undefined =
      view?.getCanvas?.() ??
      (view?.getRenderable?.() as HTMLCanvasElement | undefined);
    const cssW = canvasEl?.clientWidth || background.width;
    DPR = background.width / cssW;
  } catch {
    DPR = window.devicePixelRatio || 1;
  }

  const scale = 4;
  const scaledUrl = (
    await renderWindow.captureImages(undefined, {
      size: [background.width * scale, background.height * scale],
      scale,
    })
  )[0];

  const scaledBackground = new Image();
  scaledBackground.src = await scaledUrl;
  await new Promise<void>((res) => (scaledBackground.onload = () => res()));

  const sx = Math.floor(Math.min(startX, endX) * DPR * scale);
  const sy = Math.floor(Math.min(startY, endY) * DPR * scale);
  const sw = Math.floor(Math.abs(endX - startX) * DPR * scale);
  const sh = Math.floor(Math.abs(endY - startY) * DPR * scale);

  const maxW = scaledBackground.width;
  const maxH = scaledBackground.height;
  const srcX = Math.max(0, Math.min(sx, maxW));
  const srcY = Math.max(0, Math.min(sy, maxH));
  const srcW = Math.max(1, Math.min(sw, maxW - srcX));
  const srcH = Math.max(1, Math.min(sh, maxH - srcY));

  const cropCanvas = document.createElement('canvas');
  cropCanvas.width = srcW;
  cropCanvas.height = srcH;
  const cropCtx = cropCanvas.getContext('2d')!;
  cropCtx.drawImage(scaledBackground, srcX, srcY, srcW, srcH, 0, 0, srcW, srcH);

  const croppedImage = cropCanvas.toDataURL('image/jpeg');
  return [originalImage, croppedImage];
}

export async function cprlesioncapture(
  context: React.RefObject<CPRContext>,
  pool: BufferPoolItem,
  branchindex: number,
  points: [number, number][],
  analysisType: AnalysisType,
  currentVOI: VOI,
  isCapture: boolean = true,
): Promise<[string[], string[]]> {
  if (!context.current) return [[], []];

  function getResolutionScaledLength(base = 70) {
    const DPR = 1; // 고정
    const widthPx = Math.round(window.screen.width * DPR);
    const heightPx = Math.round(window.screen.height * DPR);
    if (widthPx === 1920 && heightPx === 1080) return base;
    const scaleW = widthPx / 1920;
    const scaleH = heightPx / 1080;
    const scale = Math.min(scaleW, scaleH);
    return Math.round(base * scale);
  }

  const { renderingQuality } = configStore.get().generalSetting;
  const lowQ = renderingQuality !== 'high';

  const lineLen = getResolutionScaledLength(70);

  const prevImageData = context.current.imageData!;
  const prevLabelData = context.current.labelData!;

  const imageData = lowQ
    ? context.current?.sharedContext.oriHUData!
    : prevImageData;
  const labelDatas = pool[lowQ ? 'oriLabelData' : 'labelData'].data as Record<
    number,
    vtkImageData
  >;
  const centerDatas = pool.centerData.data as Record<number, vtkPolyData>;
  const [url /*mw, mh*/] = await captureCPROffscreen(
    context,
    imageData,
    labelDatas[branchindex],
    centerDatas[branchindex],
    lineLen!,
    1024!,
    0,
    1,
    analysisType,
    currentVOI,
    isCapture,
    lowQ ? prevImageData : undefined,
    lowQ ? prevLabelData : undefined,
  );

  if (!isCapture) return [[], []];

  // 전체 CPR 이미지 로드
  const baseImg = new Image();
  baseImg.src = await url[0];
  await new Promise<void>((res) => (baseImg.onload = () => res()));

  const { renderer, actorIds } = context.current!;
  const actor = actorIds['CPRActor'] as vtkImageSlice;
  const mapper = actor.getMapper() as unknown as vtkImageCPRMapper;

  // 뷰포트 path → 캡처 좌표 path
  const pathOnCaptured = buildCapturedImagePath(
    actor,
    mapper,
    renderer!,
    context.current!.width!,
    context.current!.height!,
    lineLen,
    1024,
  );

  const maxW = baseImg.width;
  const frontmargin = pathOnCaptured[0].y;

  // ---------------------------
  // 1) 크롭 이미지들 생성
  // ---------------------------
  const cropimages: string[] = [];
  for (const pt of points) {
    const start = getResolutionScaledLength(
      pathOnCaptured[pt[0]].y - frontmargin,
    );
    const end = getResolutionScaledLength(
      pathOnCaptured[pt[1]].y - frontmargin,
    );
    const srcY = Math.max(0, start);
    const srcH = Math.max(1, end - start);
    const srcX = 0;
    const srcW = maxW;

    const cropCanvas = document.createElement('canvas');
    cropCanvas.width = srcW;
    cropCanvas.height = srcH;
    const cropCtx = cropCanvas.getContext('2d')!;
    cropCtx.imageSmoothingEnabled = false;
    cropCtx.drawImage(baseImg, srcX, srcY, srcW, srcH, 0, 0, srcW, srcH);

    cropimages.push(cropCanvas.toDataURL('image/png'));
  }

  // ---------------------------
  // 2) “크롭 개수만큼” 전체 CPR에 해당 구간만 강조한 이미지를 각각 생성
  // ---------------------------
  const annotatedFullList: string[] = [];

  // 스타일(원하면 조정)
  const BAND_ALPHA = 0.2; // 반투명 밴드

  // 헬퍼: HEX + alpha → rgba()
  const hexWithAlpha = (hex: string, alpha: number) => {
    const h = hex.replace('#', '');
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  };

  for (let i = 0; i < points.length; i++) {
    const [i0, i1] = points[i];
    const start = getResolutionScaledLength(pathOnCaptured[i0].y - frontmargin);
    const end = getResolutionScaledLength(pathOnCaptured[i1].y - frontmargin);
    const y = Math.max(0, Math.min(start, end));
    const h = Math.max(1, Math.abs(end - start));
    const x = 0;
    const w = maxW;

    // 개별 주석 캔버스 생성
    const annCanvas = document.createElement('canvas');
    annCanvas.width = baseImg.width;
    annCanvas.height = baseImg.height;
    const annCtx = annCanvas.getContext('2d')!;
    annCtx.imageSmoothingEnabled = false;

    // 배경 전체 그리기
    annCtx.drawImage(baseImg, 0, 0);

    // 해당 구간만 하이라이트
    annCtx.save();
    annCtx.fillStyle = hexWithAlpha(
      configStore.get().colors.isrcontent[i],
      BAND_ALPHA,
    );
    annCtx.fillRect(x, y, w, h);
    annCtx.restore();

    // 테두리
    // annCtx.lineWidth = STROKE_W;
    // annCtx.strokeStyle = COLOR;
    // annCtx.strokeRect(x + STROKE_W/2, y + STROKE_W/2, w - STROKE_W, h - STROKE_W);

    annotatedFullList.push(annCanvas.toDataURL('image/png'));
  }

  // 최종 반환: [각 구간별 주석된 전체 CPR들, 크롭들]
  return [annotatedFullList, cropimages];
}

type Point2D = { x: number; y: number };

/** 1) 뷰포트 픽셀 좌표계 path -> 캡처 이미지 좌표계 path (선형 스케일만) */
export function mapPathToCapturedImage(
  viewportPath: Point2D[],
  viewportWidth: number,
  viewportHeight: number,
  capturedWidth: number,
  capturedHeight: number,
): Point2D[] {
  const sx = capturedWidth / viewportWidth;
  const sy = capturedHeight / viewportHeight;
  return viewportPath.map((p) => ({ x: p.x * sx, y: p.y * sy }));
}

/** pathFunc와 동일한 결과의 "뷰포트 좌표계" path */
export function buildViewportPathSimple(
  actor: vtkImageSlice,
  mapper: vtkImageCPRMapper,
  renderer: vtkRenderer,
  viewportWidth: number,
  viewportHeight: number,
  movey = 0,
): Point2D[] {
  const centerline = mapper.getOrientedCenterline();
  const distances = centerline.getDistancesToFirstPoint() as number[];
  const orientations = centerline.getOrientations() as any[] | undefined;
  const centerPoint = mapper.getCenterPoint() as vec3 | null;
  const pointsArr = centerline.getPoints().getData() as Float32Array | number[];
  const numPoints = pointsArr.length / 3;

  const computeLocalXDirection = (index: number) => {
    if (orientations && orientations[index]) {
      const tangentDirection = (
        mapper.getTangentDirection
          ? (mapper.getTangentDirection() as vec3)
          : ([1, 0, 0] as vec3)
      ) as vec3;
      const localX = vec3.create();
      return vec3.transformQuat(localX, tangentDirection, orientations[index]);
    }
    return [1, 0, 0] as vec3;
  };

  const pts: [number, number, number][] = [];
  for (let i = 0; i < numPoints; i++) {
    const point = pointsArr.slice(i * 3, i * 3 + 3) as vec3;
    let x = mapper.getWidth() / 2;
    if (centerPoint) {
      const localX = computeLocalXDirection(i);
      x += vec3.dot(vec3.subtract(vec3.create(), point, centerPoint), localX);
    }
    pts.push([x, distances[i], 0]);
  }

  const matrix = actor.getUserMatrix();
  const worldCoord = vec3.create();
  const aspect = viewportWidth / viewportHeight;
  const out: Point2D[] = new Array(pts.length);

  for (let i = 0; i < pts.length; i++) {
    vec3.transformMat4(worldCoord, pts[i], matrix);
    const ndc = renderer.worldToNormalizedDisplay(
      worldCoord[0],
      worldCoord[1],
      worldCoord[2],
      aspect,
    );
    out[i] = { x: ndc[0] * viewportWidth, y: ndc[1] * viewportHeight + movey };
  }
  return out;
}

/** 2) actor/mapper/renderer로 뷰포트 path를 만든 뒤, 캡처 이미지 좌표로 매핑 */
export function buildCapturedImagePath(
  actor: vtkImageSlice,
  mapper: vtkImageCPRMapper,
  renderer: vtkRenderer,
  viewportWidth: number,
  viewportHeight: number,
  capturedWidth: number,
  capturedHeight: number,
  movey = 0,
): Point2D[] {
  const viewportPath = buildViewportPathSimple(
    actor,
    mapper,
    renderer,
    viewportWidth,
    viewportHeight,
    movey,
  );
  return mapPathToCapturedImage(
    viewportPath,
    viewportWidth,
    viewportHeight,
    capturedWidth,
    capturedHeight,
  );
}

async function oneCaptureVR(
  context: React.MutableRefObject<VRContext | null>,
  pool: BufferPoolItem,
  seriesId: string,
  analysisType: AnalysisType,
  name: string,
  currentVOI: VOI,
  visible3DKeys: string[],
  branchids?: number[],
) {
  if (!context.current) {
    return;
  }
  const { windowLevel, windowWidth } = currentVOI;

  const quality = configStore.get().generalSetting.renderingQuality;
  const {
    renderer,
    actorIds,
    savedTranslation,
    renderWindow,
    canvas,
    overlayRenderer,
  } = context.current;
  const backupActorIds = { ...context.current.actorIds };
  const volumeDataBackups: any[] = [];
  for (const [key, actor] of Object.entries(actorIds)) {
    if ((actor as any).isA?.('vtkVolume')) {
      const vol = actor as vtkVolume;
      const mapper = vol.getMapper?.();
      const imageData = mapper?.getInputData?.();
      if (!imageData) continue;
      volumeDataBackups.push({
        key,
        scalarsBackup: imageData.getPointData().getScalars().getData(),
      });
    }
  }

  context.current.actorIds = {};
  const fit = configStore.get().modelCaptureFit;
  const oriCamera = getOriCamera(renderer);

  const visibleCalcium =
    visible3DKeys.includes('calcium') && name !== volumeNames.MIP;
  renderer.removeAllVolumes();
  overlayRenderer?.removeAllActors();
  await runSegmentationVolume(context, pool, seriesId!, analysisType, {
    chamberEnabled: false,
    heartCut: name === volumeNames.heartCut,
    visibleMIP: name === volumeNames.MIP,
    ww: windowWidth,
    wl: windowLevel,
    visibleKeys: visible3DKeys,
    renderingQuality: quality,
    useOriginalGeometry: quality === 'low',
  });
  const targetVolumeActor =
    context.current.actorIds[name] ??
    context.current.actorIds[volumeNames.base] ??
    context.current.actorIds[volumeNames.heartCut] ??
    context.current.actorIds[volumeNames.MIP];
  targetVolumeActor?.setVisibility(true);
  context.current.actorIds[volumeNames.calcium]?.setVisibility(visibleCalcium);
  if ((true || fit) && targetVolumeActor) {
    fitActorToCanvas(renderer, targetVolumeActor.getBounds(), canvas, 0.9);
  }

  const urls: string[] = [];
  const targetBranchIds =
    branchids && branchids.length > 0 ? branchids : [undefined];
  for (const branchId of targetBranchIds) {
    if (branchId !== undefined) {
      centerLineRender(context as any, pool, analysisType, [branchId]);
      renderWindow.render();
    }
    const url = await captureView(
      {
        type: 'vtk',
        renderWindow,
      },
      { scale: 1 },
    );
    urls.push(url);
  }

  applyScaledCameraPan(renderer, renderWindow, oriCamera, !fit);

  context.current.actorIds = backupActorIds;
  renderer.removeAllVolumes();
  overlayRenderer?.removeAllActors();

  for (const [key, actor] of Object.entries(backupActorIds)) {
    if ((actor as any).isA?.('vtkVolume')) {
      const volume = actor as vtkVolume;
      volume.setVisibility(
        key === volumeNames.calcium ? visibleCalcium : key === name,
      );
      const volumeBackup = volumeDataBackups.find((x) => x.key === key);
      const volumeMapper = volume.getMapper?.();
      const imageData = volumeMapper?.getInputData?.();
      if (!imageData || !volumeMapper || !volumeBackup) continue;
      const data = volumeBackup.scalarsBackup;
      const pointData = imageData.getPointData();
      pointData?.getScalars()?.setData(data);
      volumeMapper.setInputData(imageData);
      volumeMapper.modified();
      volume.modified();
      renderer.addVolume(volume);
    }
    if (key.startsWith('centerLine.') || key.startsWith('node.')) {
      actor.setVisibility(true);
      overlayRenderer?.addActor(actor);
    }
  }

  context.current.savedTranslation = savedTranslation;
  setPickingForVolume(context, analysisType);
  return urls;
}

export async function oneCapture3D(
  poolRef: React.MutableRefObject<ContextPool>,
  selectedSeriesId: string,
  analysisType: AnalysisType,
  capture3DType: AutoCaptureMode,
  pool: BufferPoolItem,
  currentVOI: VOI,
  visible3DKeys: string[],
  currentbranchId: number,
  branchid?: number[],
) {
  const keyname = capture3DType === 'Surface3DCapture' ? `Surface3D` : 'VR';
  const contextsurface3d = {
    current:
      keyname === 'VR'
        ? (poolRef.current['VR'] as VRContext)
        : (poolRef.current['Surface3D'] as SurfaceContext),
  };

  if (!contextsurface3d || !contextsurface3d.current) return;
  const {
    genericRenderWindow,
    renderer,
    orientationWidget,
    renderWindow,
    actorIds,
    canvas,
  } = contextsurface3d.current;
  const ref = genericRenderWindow.getContainer();

  setNewContainer(
    genericRenderWindow,
    configStore.get().captureWidth,
    configStore.get().captureHeight,
  );

  //캡처시 3d axis 비활성화
  setActorVisible(contextsurface3d, false, [nodeNames.node]);
  setAxesLinesVisibility({ context: contextsurface3d, visible: false });
  // setActorsCenter(contextsurface3d); //리턴없이 d3context 내부 변수로 스윽 set

  orientationWidget?.setEnabled(false);
  const oriCamera = getOriCamera(renderer);
  renderer.resetCamera();
  renderWindow.render();

  let urls: string[] = [];
  const targetBranchIds =
    branchid && branchid.length > 0 ? branchid : [undefined];


  const config = configStore.get();
  const {
    heartCut,
    visibleMIP,
    laText: isLaCaptureMode,
    modelCaptureFit,
    generalSetting,
    coronaryAorta,
  } = config;

  let name = isLaCaptureMode
    ? volumeNames.base
    : visibleMIP
      ? volumeNames.MIP
      : !coronaryAorta && heartCut
        ? volumeNames.heartCut
        : volumeNames.base;

  if (
    keyname === 'VR' &&
    visible3DKeys.length !== 0 &&
    generalSetting.renderingQuality === 'low'
  ) {
    urls =
      (await oneCaptureVR(
        contextsurface3d as React.MutableRefObject<VRContext | null>,
        pool!,
        selectedSeriesId,
        analysisType,
        name,
        currentVOI,
        visible3DKeys,
        branchid,
      )) ?? [];
  } else if (isLaCaptureMode) {
    applyPoolCaptureCamera({
      pool,
      renderer,
      renderWindow,
      canvas,
      modelCaptureFit,
      oriCamera,
    });
    const url = await captureView(
      {
        type: 'vtk',
        renderWindow: contextsurface3d.current!.renderWindow,
      },
      { scale: 1 },
    );
    urls.push(url);
  } else {
    const actorName =
      keyname === 'Surface3D'
        ? (getActorNameByAnalysisType(analysisType) ?? 'coronary')
        : name;
    const actor = actorIds[actorName];
    if (!actor) return;

    if (true || configStore.get().modelCaptureFit) {
      fitActorToCanvas(renderer, actor.getBounds(), canvas, 0.9);
    }

    for (const b of targetBranchIds) {
      if (b !== undefined) {
        centerLineRender(contextsurface3d, pool!, analysisType, [b]);
        renderWindow.render();
      }
      const url = await captureView(
        {
          type: 'vtk',
          renderWindow: contextsurface3d.current!.renderWindow,
        },
        { scale: 1 },
      );
      urls.push(url);
    }

    //원래대로
    console.log('currentbranchId', currentbranchId);
    if (currentbranchId !== undefined) {
      centerLineRender(contextsurface3d, pool!, analysisType, [
        currentbranchId,
      ]);
      renderWindow.render();
    }
  }

  if (ref) genericRenderWindow.setContainer(ref);

  //이전 위치로 싸악 이동
  orientationWidget?.setEnabled(true);
  applyScaledCameraPan(renderer, renderWindow, oriCamera);
  const visibleCoronary = visible3DKeys.includes('coronary');
  setActorVisible(contextsurface3d, visibleCoronary, [nodeNames.node]);
  setAxesLinesVisibility({
    context: contextsurface3d,
    visible: contextsurface3d?.current.isAxis3DVisible,
  });

  genericRenderWindow.resize();
  renderWindow.render();

  return urls;
}

type CPRSeriesPart = 'model' | 'stretched' | 'straightened';
type CPRSeriesMode = 'combined' | 'splitCpr' | 'splitAll';
type CPRSeriesPartPayload = { buffers: BufferData[]; crops: number[] };

function getCPRSeriesPolicy() {
  const cfg = configStore.get() as any;
  const modeRaw = String(cfg.cprSeriesMode ?? '').trim();
  const mode: CPRSeriesMode =
    modeRaw === 'splitCpr' || modeRaw === 'splitAll' || modeRaw === 'combined'
      ? (modeRaw as CPRSeriesMode)
      : cfg.cprSeriesSeparation
        ? 'splitCpr'
        : 'combined';

  const includeModelInSplitCpr = Boolean(cfg.cprSplitIncludeModel ?? true);
  const includeModelStretched =
    cfg.cprSplitIncludeModelStretched !== undefined
      ? Boolean(cfg.cprSplitIncludeModelStretched)
      : includeModelInSplitCpr;
  const includeModelStraightened =
    cfg.cprSplitIncludeModelStraightened !== undefined
      ? Boolean(cfg.cprSplitIncludeModelStraightened)
      : includeModelInSplitCpr;
  const allowed: CPRSeriesPart[] = ['model', 'stretched', 'straightened'];
  const rawOrder: string[] = Array.isArray(cfg.cprSeriesOrder)
    ? cfg.cprSeriesOrder
    : allowed;
  const order = rawOrder
    .filter((v): v is CPRSeriesPart => allowed.includes(v as CPRSeriesPart))
    .filter((v, i, arr) => arr.indexOf(v) === i);
  const normalizedOrder =
    order.length === allowed.length
      ? order
      : [...order, ...allowed.filter((x) => !order.includes(x))];

  return {
    mode,
    includeModelInSplitCpr,
    includeModelStretched,
    includeModelStraightened,
    order: normalizedOrder,
  };
}

function makeCPRSendData(
  series_description: string,
  currentVOI: VOI,
  buffers: BufferData[],
  cropsize: number[],
): Send_PACS_Data {
  const { windowLevel, windowWidth } = currentVOI;
  return {
    buffers,
    screen_capture: false,
    capture_kind: 'CPR',
    series_description,
    cropsize,
    windowWidth: String(windowWidth),
    windowCenter: String(windowLevel),
  };
}

function composeCPRSeriesData(
  seriesDescription: string,
  currentVOI: VOI,
  parts: Record<CPRSeriesPart, CPRSeriesPartPayload>,
): Send_PACS_Data[] {
  const {
    mode,
    includeModelInSplitCpr,
    includeModelStretched,
    includeModelStraightened,
    order,
  } = getCPRSeriesPolicy();
  const nonEmpty = (part: CPRSeriesPart) => parts[part].buffers.length > 0;

  if (mode === 'combined') {
    const buffers = order.flatMap((part) => parts[part].buffers);
    const crops = order.flatMap((part) => parts[part].crops);
    return buffers.length
      ? [makeCPRSendData(seriesDescription, currentVOI, buffers, crops)]
      : [];
  }

  if (mode === 'splitAll') {
    const titleMap: Record<CPRSeriesPart, string> = {
      model: 'Model',
      stretched: 'Stretched',
      straightened: 'Straightened',
    };
    return order
      .filter((part) => nonEmpty(part))
      .map((part) =>
        makeCPRSendData(
          `${seriesDescription}_${titleMap[part]}`,
          currentVOI,
          parts[part].buffers,
          parts[part].crops,
        ),
      );
  }

  const cprOrder = order.filter(
    (part): part is 'stretched' | 'straightened' => part !== 'model',
  );
  const model = parts.model;
  const modelOrderIndex = order.indexOf('model');
  const out: Send_PACS_Data[] = [];
  for (const part of cprOrder) {
    if (!nonEmpty(part)) continue;
    const partOrderIndex = order.indexOf(part);
    const includeModelForPart =
      part === 'stretched' ? includeModelStretched : includeModelStraightened;
    const useModel = includeModelInSplitCpr && includeModelForPart;
    const placeModelBefore =
      modelOrderIndex >= 0 &&
      partOrderIndex >= 0 &&
      modelOrderIndex < partOrderIndex;
    const buffers = useModel
      ? placeModelBefore
        ? [...model.buffers, ...parts[part].buffers]
        : [...parts[part].buffers, ...model.buffers]
      : [...parts[part].buffers];
    const crops = useModel
      ? placeModelBefore
        ? [...model.crops, ...parts[part].crops]
        : [...parts[part].crops, ...model.crops]
      : [...parts[part].crops];
    // series 이름 여기서 정하면됨 일단 part는 뺐음
    out.push(
      makeCPRSendData(`${seriesDescription}`, currentVOI, buffers, crops),
    );
  }
  return out;
}

function mergeCPRSeriesChunks(chunks: Send_PACS_Data[][]): Send_PACS_Data[] {
  const merged: Send_PACS_Data[] = [];
  for (const list of chunks) {
    for (let i = 0; i < list.length; i++) {
      const item = list[i];
      if (!merged[i]) {
        merged[i] = {
          ...item,
          buffers: [...item.buffers],
          cropsize: item.cropsize ? [...item.cropsize] : [],
        };
        continue;
      }
      const prev = merged[i];
      prev.buffers.push(...item.buffers);
      if (prev.cropsize && item.cropsize) prev.cropsize.push(...item.cropsize);
    }
  }
  return merged;
}

export async function cprSingleCapture(
  poolRef: React.MutableRefObject<ContextPool>,
  series_description: string,
  selectedSeriesId: string,
  analysisType: AnalysisType,
  capture3DType: AutoCaptureMode,
  pool: BufferPoolItem,
  currentVOI: VOI,
  visible3DKeys: string[],
  branchid?: number,
): Promise<Send_PACS_Data[] | undefined> {
  const model3DBuffers: BufferData[] = [];
  const stretchedBuffers: BufferData[] = [];
  const straightenedBuffers: BufferData[] = [];
  const model3DCrops: number[] = [];
  const stretchedCrops: number[] = [];
  const straightenedCrops: number[] = [];
  const branchdata = pool.branchGraphDataset.data as BranchInfo;
  const graphComposer = createCPRGraphComposer(pool, branchid);
  const graphHUComposer = createCPRGraphHUComposer(pool, branchid);
  const graphMeta = getCPRGraphMeta(branchdata, branchid ?? 0);
  const diameter_length = branchdata.getLengthArray(branchid ?? 0)?.at(-1) ?? 0;
  const meanValue = graphMeta.mean;

  const url3dArr = await oneCapture3D(
    poolRef,
    selectedSeriesId,
    analysisType,
    capture3DType,
    pool,
    currentVOI,
    visible3DKeys,
    branchid !== undefined ? branchid: 0,
    branchid !== undefined ? [branchid] : [],
  );
  const url3d = url3dArr?.[0];

  if (url3d) {
    const buffer3d = await imageUrlToArrayBufferSimple(url3d);
    buffer3d.branch_name = series_description;
    model3DBuffers.push(buffer3d);
    model3DCrops.push(-1);
  }

  if (!configStore.get().label.lumen && !configStore.get().label.plaque) {
    const centerlineData =
      branchid === undefined
        ? undefined
        : (pool.centerData.data as Record<number, vtkPolyData>)[branchid];
    const huByMode = await cprhucaptureByMode(
      poolRef,
      pool,
      configStore.get().captureWidth,
      configStore.get().captureHeight,
      analysisType,
      currentVOI,
      true,
      centerlineData,
    );
    await cprhucaptureByMode(
      poolRef,
      pool,
      configStore.get().captureWidth,
      configStore.get().captureHeight,
      analysisType,
      currentVOI,
      false,
      centerlineData,
    );

    for (const d_ of huByMode.stretched) {
      const bd: BufferData = {
        buffer: d_.data.buffer as ArrayBuffer,
        width: d_.width,
        height: d_.height,
        extension: 'bin',
        branch_name: series_description,
      };
      stretchedBuffers.push(bd);
      stretchedCrops.push(Math.max(d_.width, d_.height));
    }
    for (const raw of huByMode.straightened) {
      const d_ = graphHUComposer ? graphHUComposer(raw) : raw;
      const bd: BufferData = {
        buffer: d_.data.buffer as ArrayBuffer,
        width: d_.width,
        height: d_.height,
        extension: 'bin',
        branch_name: series_description,
      };
      if (graphMeta.metric !== 'none') {
        bd.cprinfo = {
          Graph_Type: graphMeta.label,
          length: Number(diameter_length.toFixed(2)),
          angle: Number(raw.angle.toFixed(2)),
          ...(graphMeta.metric === 'area'
            ? { mean_area: Number(meanValue.toFixed(2)) }
            : { mean_diameter: Number(meanValue.toFixed(2)) }),
        };
      }
      straightenedBuffers.push(bd);
      straightenedCrops.push(Math.max(d_.width, d_.height));
    }
  } else {
    const lowQ = configStore.get().generalSetting.renderingQuality !== 'high';
    const labelData = (
      pool[lowQ ? 'oriLabelData' : 'labelData'].data as Record<
        number,
        vtkImageData
      >
    )[branchid!];
    // (pool.oriLabelData.data as Record<number,vtkImageData>)[branchid!]:
    // (pool.oriLabelData.data as Record<number,vtkImageData>)[branchid!]:
    const urlsByMode = await cprcaptureByMode({
      poolRef,
      pool,
      captureWidth: configStore.get().captureWidth,
      captureHeight: configStore.get().captureHeight,
      analysisType,
      currentVOI,
      isCapture: true,
      labelData,
    });
    await cprcaptureByMode({
      poolRef,
      pool,
      captureWidth: configStore.get().captureWidth,
      captureHeight: configStore.get().captureHeight,
      analysisType,
      currentVOI,
      isCapture: false,
      labelData,
    });

    for (const [url_] of urlsByMode.stretched) {
      for (const rawUrl of url_) {
        const buffer = await imageUrlToArrayBufferSimple(rawUrl);
        buffer.branch_name = series_description;
        stretchedBuffers.push(buffer);
        stretchedCrops.push(Math.max(buffer.width, buffer.height));
      }
    }
    for (const [url_, mw, mh, angles] of urlsByMode.straightened) {
      for (let i = 0; i < url_.length; i++) {
        const mergedUrl = graphComposer
          ? await graphComposer(url_[i])
          : url_[i];
        const buffer = await imageUrlToArrayBufferSimple(mergedUrl);
        if (graphMeta.metric !== 'none') {
          buffer.cprinfo = {
            Graph_Type: graphMeta.label,
            length: Number(diameter_length.toFixed(2)),
            angle: Number(angles[i].toFixed(2)),
            ...(graphMeta.metric === 'area'
              ? { mean_area: Number(meanValue.toFixed(2)) }
              : { mean_diameter: Number(meanValue.toFixed(2)) }),
          };
        }
        buffer.branch_name = series_description;
        straightenedBuffers.push(buffer);
        straightenedCrops.push(Math.max(buffer.width, buffer.height));
      }
    }
  }
  return composeCPRSeriesData(series_description, currentVOI, {
    model: { buffers: model3DBuffers, crops: model3DCrops },
    stretched: { buffers: stretchedBuffers, crops: stretchedCrops },
    straightened: { buffers: straightenedBuffers, crops: straightenedCrops },
  });
}

function indicesOf(arr: number[], v: number): number[] {
  const idxs: number[] = [];
  for (let i = 0; i < arr.length; i++) {
    if (Object.is(arr[i], v)) idxs.push(i); // NaN까지 정확히 처리
  }
  return idxs;
}

export async function cprMultiCapture(
  poolRef: React.MutableRefObject<ContextPool>,
  pool: BufferPoolItem,
  type: AutoCaptureMode,
  selectedSeriesId: string,
  analysisType: AnalysisType,
  capture3DType: AutoCaptureMode,
  newtype: [string, number][],
  currentVOI: VOI,
  visible3DKeys: string[],
  currentbranchId:number,
) {
  const con = {
    current: poolRef.current[`CPR.0.stretched` as ContextKey] as CPRContext,
  } as React.MutableRefObject<CPRContext | null>;

  const { renderingQuality } = configStore.get().generalSetting;
  const lowQ = renderingQuality !== 'high';
  // const labelDatas = pool.labelData.data as Record<number, vtkImageData>;
  const labelDatas = pool[lowQ ? 'oriLabelData' : 'labelData'].data as Record<
    number,
    vtkImageData
  >;
  const centerDatas = pool.centerData.data as Record<number, vtkPolyData>;
  const branchdata = pool.branchGraphDataset.data as BranchInfo;

  if (!labelDatas && !centerDatas && !branchdata) {
    console.log('not data', labelDatas, centerDatas, branchdata);
    return [];
  }
  let arr: number[] = [];
  const condition = configStore.get().mainBranchOrder;
  const checkPNG =
    !configStore.get().label.lumen && !configStore.get().label.plaque;
  const groupByMainBranch = !!configStore.get().cprGroupByMainBranch;
  const model3DUrlByBranch = new Map<number, string>();

  const preload3DForBranches = async (branchIds: number[]) => {
    const targets = Array.from(new Set(branchIds)).filter(
      (id) => !model3DUrlByBranch.has(id),
    );
    if (targets.length === 0) return;
    const urls = await oneCapture3D(
      poolRef,
      selectedSeriesId,
      analysisType,
      capture3DType,
      pool,
      currentVOI,
      visible3DKeys,
      currentbranchId,
      targets,
    );
    targets.forEach((id, i) => {
      const url = urls?.[i];
      if (url) model3DUrlByBranch.set(id, url);
    });
  };

  const addBranchCapture = async (
    key: number,
    isCapture: boolean,
  ): Promise<{
    model3DBuffers: BufferData[];
    stretchedBuffers: BufferData[];
    straightenedBuffers: BufferData[];
    model3DCrops: number[];
    stretchedCrops: number[];
    straightenedCrops: number[];
    buffers: BufferData[];
    cropsize_temp: number[];
  } | null> => {
    const branchName = newtype
      ? key !== undefined
        ? (newtype[key]?.[0] ?? '')
        : ''
      : '';
    const graphComposer = createCPRGraphComposer(pool, key);
    const graphHUComposer = createCPRGraphHUComposer(pool, key);
    const graphMeta = getCPRGraphMeta(branchdata, key);
    const diameter_length = branchdata.getLengthArray(key ?? 0)?.at(-1) ?? 0;
    const meanValue = graphMeta.mean;
    const expectedStretchedCount = Math.max(
      0,
      configStore.get().stretchedCount ?? 0,
    );
    const expectedStraightenedCount = Math.max(
      0,
      configStore.get().straightenedCount ?? 0,
    );
    const model3DBuffers: BufferData[] = [];
    const stretchedBuffers: BufferData[] = [];
    const straightenedBuffers: BufferData[] = [];
    const model3DCrops: number[] = [];
    const stretchedCrops: number[] = [];
    const straightenedCrops: number[] = [];

    const labelData = labelDatas[key];
    const centerData = centerDatas[key];
    let urlsByMode:
      | Record<CPRCaptureMode, [string[], number, number, number[]][]>
      | undefined;
    let huByMode: Record<CPRCaptureMode, CPRExportResult[]> | undefined;
    if (checkPNG) {
      const stretchedOnly = await cprhucaptureByMode(
        poolRef,
        pool,
        configStore.get().captureWidth,
        configStore.get().captureHeight,
        analysisType,
        currentVOI,
        isCapture,
        centerData,
        ['stretched'],
      );
      const straightenedOnly = await cprhucaptureByMode(
        poolRef,
        pool,
        configStore.get().captureWidth,
        configStore.get().captureHeight,
        analysisType,
        currentVOI,
        isCapture,
        centerData,
        ['straightened'],
      );
      huByMode = {
        stretched: stretchedOnly.stretched ?? [],
        straightened: straightenedOnly.straightened ?? [],
      };
    } else {
      const stretchedOnly = await cprcaptureByMode({
        poolRef,
        pool,
        captureWidth: configStore.get().captureWidth,
        captureHeight: configStore.get().captureHeight,
        analysisType,
        currentVOI,
        isCapture,
        labelData,
        centerlineData: centerData,
        modes: ['stretched'],
      });
      const straightenedOnly = await cprcaptureByMode({
        poolRef,
        pool,
        captureWidth: configStore.get().captureWidth,
        captureHeight: configStore.get().captureHeight,
        analysisType,
        currentVOI,
        isCapture,
        labelData,
        centerlineData: centerData,
        modes: ['straightened'],
      });
      urlsByMode = {
        stretched: stretchedOnly.stretched ?? [],
        straightened: straightenedOnly.straightened ?? [],
      };
    }

    if (!isCapture) return null;

    let url3d = model3DUrlByBranch.get(key);
    if (!url3d) {
      const fallbackUrls = await oneCapture3D(
        poolRef,
        selectedSeriesId,
        analysisType,
        capture3DType,
        pool,
        currentVOI,
        visible3DKeys,
        currentbranchId,
        [key],
      );
      url3d = fallbackUrls?.[0];
      if (url3d) {
        model3DUrlByBranch.set(key, url3d);
      }
    }
    if (url3d) {
      const buffer3d = await imageUrlToArrayBufferSimple(url3d);
      buffer3d.branch_name = branchName;
      model3DBuffers.push(buffer3d);
      model3DCrops.push(-1);
    }

    if (checkPNG) {
      const stretchedHU = huByMode?.stretched ?? [];
      const straightenedHU = huByMode?.straightened ?? [];

      for (const raw of stretchedHU) {
        const d_ = raw;
        const bd: BufferData = {
          buffer: d_.data.buffer as ArrayBuffer,
          width: d_.width,
          height: d_.height,
          extension: 'bin',
          branch_name: branchName,
        };
        stretchedBuffers.push(bd);
        stretchedCrops.push(Math.max(d_.width, d_.height));
      }

      for (const raw of straightenedHU) {
        const d_ = graphHUComposer ? graphHUComposer(raw) : raw;
        const bd: BufferData = {
          buffer: d_.data.buffer as ArrayBuffer,
          width: d_.width,
          height: d_.height,
          extension: 'bin',
          branch_name: branchName,
        };
        if (graphMeta.metric !== 'none') {
          bd.cprinfo = {
            Graph_Type: graphMeta.label,
            length: Number(diameter_length.toFixed(2)),
            angle: Number(raw.angle.toFixed(2)),
            ...(graphMeta.metric === 'area'
              ? { mean_area: Number(meanValue.toFixed(2)) }
              : { mean_diameter: Number(meanValue.toFixed(2)) }),
          };
        }
        straightenedBuffers.push(bd);
        straightenedCrops.push(Math.max(d_.width, d_.height));
      }
    } else {
      const stretchedPNG = urlsByMode?.stretched ?? [];
      const straightenedPNG = urlsByMode?.straightened ?? [];

      for (const [url_] of stretchedPNG) {
        for (const rawUrl of url_) {
          const buffer = await imageUrlToArrayBufferSimple(rawUrl);
          buffer.branch_name = branchName;
          stretchedBuffers.push(buffer);
          stretchedCrops.push(Math.max(buffer.width, buffer.height));
        }
      }

      for (const [url_, _mw, _mh, angles] of straightenedPNG) {
        for (let i = 0; i < url_.length; i++) {
          const rawUrl = url_[i];
          const mergedUrl = graphComposer
            ? await graphComposer(rawUrl)
            : rawUrl;
          const buffer = await imageUrlToArrayBufferSimple(mergedUrl);
          buffer.branch_name = branchName;
          if (graphMeta.metric !== 'none') {
            buffer.cprinfo = {
              Graph_Type: graphMeta.label,
              length: Number(diameter_length.toFixed(2)),
              angle: Number(angles?.[i].toFixed(2)),
              ...(graphMeta.metric === 'area'
                ? { mean_area: Number(meanValue.toFixed(2)) }
                : { mean_diameter: Number(meanValue.toFixed(2)) }),
            };
          }
          straightenedBuffers.push(buffer);
          straightenedCrops.push(Math.max(buffer.width, buffer.height));
        }
      }
    }

    const mismatchStretched =
      expectedStretchedCount > 0 &&
      stretchedBuffers.length !== expectedStretchedCount;
    const mismatchStraightened =
      expectedStraightenedCount > 0 &&
      straightenedBuffers.length !== expectedStraightenedCount;

    if (mismatchStretched || mismatchStraightened) {
      if (checkPNG) {
        const retryModes: CPRCaptureMode[] = [];
        if (mismatchStretched) retryModes.push('stretched');
        if (mismatchStraightened) retryModes.push('straightened');

        const retryHUByMode = await cprhucaptureByMode(
          poolRef,
          pool,
          configStore.get().captureWidth,
          configStore.get().captureHeight,
          analysisType,
          currentVOI,
          true,
          centerData,
          retryModes,
        );

        if (mismatchStretched) {
          stretchedBuffers.length = 0;
          stretchedCrops.length = 0;
          for (const raw of retryHUByMode.stretched ?? []) {
            const bd: BufferData = {
              buffer: raw.data.buffer as ArrayBuffer,
              width: raw.width,
              height: raw.height,
              extension: 'bin',
              branch_name: branchName,
            };
            stretchedBuffers.push(bd);
            stretchedCrops.push(Math.max(raw.width, raw.height));
          }
        }

        if (mismatchStraightened) {
          straightenedBuffers.length = 0;
          straightenedCrops.length = 0;
          for (const raw of retryHUByMode.straightened ?? []) {
            const d_ = graphHUComposer ? graphHUComposer(raw) : raw;
            const bd: BufferData = {
              buffer: d_.data.buffer as ArrayBuffer,
              width: d_.width,
              height: d_.height,
              extension: 'bin',
              branch_name: branchName,
            };
            if (graphMeta.metric !== 'none') {
              bd.cprinfo = {
                Graph_Type: graphMeta.label,
                length: Number(diameter_length.toFixed(2)),
                angle: Number(raw.angle.toFixed(2)),
                ...(graphMeta.metric === 'area'
                  ? { mean_area: Number(meanValue.toFixed(2)) }
                  : { mean_diameter: Number(meanValue.toFixed(2)) }),
              };
            }
            straightenedBuffers.push(bd);
            straightenedCrops.push(Math.max(d_.width, d_.height));
          }
        }
      } else {
        const retryModes: CPRCaptureMode[] = [];
        if (mismatchStretched) retryModes.push('stretched');
        if (mismatchStraightened) retryModes.push('straightened');

        const retryByMode = await cprcaptureByMode({
          poolRef,
          pool,
          captureWidth: configStore.get().captureWidth,
          captureHeight: configStore.get().captureHeight,
          analysisType,
          currentVOI,
          isCapture: true,
          labelData,
          centerlineData: centerData,
          modes: retryModes,
        });

        if (mismatchStretched) {
          stretchedBuffers.length = 0;
          stretchedCrops.length = 0;
          for (const [url_] of retryByMode.stretched ?? []) {
            for (const rawUrl of url_) {
              const buffer = await imageUrlToArrayBufferSimple(rawUrl);
              buffer.branch_name = branchName;
              stretchedBuffers.push(buffer);
              stretchedCrops.push(Math.max(buffer.width, buffer.height));
            }
          }
        }

        if (mismatchStraightened) {
          straightenedBuffers.length = 0;
          straightenedCrops.length = 0;
          for (const [url_, _mw, _mh, angles] of retryByMode.straightened ??
            []) {
            for (let i = 0; i < url_.length; i++) {
              const rawUrl = url_[i];
              const mergedUrl = graphComposer
                ? await graphComposer(rawUrl)
                : rawUrl;
              const buffer = await imageUrlToArrayBufferSimple(mergedUrl);
              buffer.branch_name = branchName;
              if (graphMeta.metric !== 'none') {
                buffer.cprinfo = {
                  Graph_Type: graphMeta.label,
                  length: Number(diameter_length.toFixed(2)),
                  angle: Number(angles?.[i].toFixed(2)),
                  ...(graphMeta.metric === 'area'
                    ? { mean_area: Number(meanValue.toFixed(2)) }
                    : { mean_diameter: Number(meanValue.toFixed(2)) }),
                };
              }
              straightenedBuffers.push(buffer);
              straightenedCrops.push(Math.max(buffer.width, buffer.height));
            }
          }
        }
      }
    }

    const buffers = [
      ...model3DBuffers,
      ...stretchedBuffers,
      ...straightenedBuffers,
    ];
    const cropsize_temp = [
      ...model3DCrops,
      ...stretchedCrops,
      ...straightenedCrops,
    ];
    return {
      model3DBuffers,
      stretchedBuffers,
      straightenedBuffers,
      model3DCrops,
      stretchedCrops,
      straightenedCrops,
      buffers,
      cropsize_temp,
    };
  };
  if (type === 'CPRMainCapture') {
    const newBranchids = numbersByOrder(newtype, condition);
    arr = [...newBranchids, con.current?.selectedBranchNum!];
  } else if (type === 'CPRFullCapture') {
    const rca = indicesOf(configStore.get().motherBranch, 0);
    const lad = indicesOf(configStore.get().motherBranch, 1);
    const lcx = indicesOf(configStore.get().motherBranch, 2);

    if (groupByMainBranch) {
      const { mode } = getCPRSeriesPolicy();
      const groupMap: Record<string, number[]> = {
        RCA: rca,
        LAD: lad,
        LCX: lcx,
      };
      const groups = condition
        .map((name) => ({
          name,
          indices: (groupMap[name] || []).filter(
            (idx) => labelDatas[idx] !== null,
          ),
        }))
        .filter((group) => group.indices.length > 0);

      const datas = [];
      await preload3DForBranches(groups.flatMap((group) => group.indices));
      for (const group of groups) {
        // split 계열 모드에서는 branch별 series를 유지한다.
        if (mode !== 'combined') {
          for (const key of group.indices) {
            const branchCapture = await addBranchCapture(key, true);
            if (!branchCapture) continue;
            const branchSeriesName = newtype
              ? key !== undefined
                ? (newtype[key]?.[0] ?? group.name)
                : group.name
              : group.name;
            datas.push(
              ...composeCPRSeriesData(branchSeriesName, currentVOI, {
                model: {
                  buffers: branchCapture.model3DBuffers,
                  crops: branchCapture.model3DCrops,
                },
                stretched: {
                  buffers: branchCapture.stretchedBuffers,
                  crops: branchCapture.stretchedCrops,
                },
                straightened: {
                  buffers: branchCapture.straightenedBuffers,
                  crops: branchCapture.straightenedCrops,
                },
              }),
            );
          }
          continue;
        }

        const branchChunks: Send_PACS_Data[][] = [];
        for (const key of group.indices) {
          const branchCapture = await addBranchCapture(key, true);
          if (!branchCapture) continue;
          branchChunks.push(
            composeCPRSeriesData(group.name, currentVOI, {
              model: {
                buffers: branchCapture.model3DBuffers,
                crops: branchCapture.model3DCrops,
              },
              stretched: {
                buffers: branchCapture.stretchedBuffers,
                crops: branchCapture.stretchedCrops,
              },
              straightened: {
                buffers: branchCapture.straightenedBuffers,
                crops: branchCapture.straightenedCrops,
              },
            }),
          );
        }
        datas.push(...mergeCPRSeriesChunks(branchChunks));
      }

      const restoreKey = con.current?.selectedBranchNum;
      if (restoreKey !== undefined && restoreKey !== null) {
        await addBranchCapture(restoreKey, false);
      }

      console.log(datas);

      return datas;
    }

    for (const name of condition) {
      if (name === 'RCA') arr.push(...rca);
      if (name === 'LAD') arr.push(...lad);
      if (name === 'LCX') arr.push(...lcx);
    }
    // arr = [...lad, ...lcx, ...rca];
    for (let i = 0; i < arr.length; i++) {
      if (labelDatas[arr[i]] === null) {
        arr = [...arr.slice(0, i), ...arr.slice(i + 1)];
      }
    }

    arr.push(con.current?.selectedBranchNum!);
  }

  let datas = [];
  await preload3DForBranches(arr.slice(0, Math.max(0, arr.length - 1)));

  for (let i = 0; i < arr.length; i++) {
    const isCapture = i !== arr.length - 1;
    const key = arr[i];

    if (isCapture) {
      const branchCapture = await addBranchCapture(key, true);
      if (!branchCapture) continue;

      // const { id, name } = branchdata.getOriginBranchId(key);
      const name = newtype ? (key !== undefined ? newtype[key][0] : '') : '';
      datas.push(
        ...composeCPRSeriesData(name, currentVOI, {
          model: {
            buffers: branchCapture.model3DBuffers,
            crops: branchCapture.model3DCrops,
          },
          stretched: {
            buffers: branchCapture.stretchedBuffers,
            crops: branchCapture.stretchedCrops,
          },
          straightened: {
            buffers: branchCapture.straightenedBuffers,
            crops: branchCapture.straightenedCrops,
          },
        }),
      );
    } else {
      await addBranchCapture(key, false);
    }
  }

  console.log(datas);

  return datas;
}

function getSelectedCPRGraphMetric(): 'diameter' | 'area' | 'none' {
  const value = String((configStore.get() as any).cprGraphType ?? 'diameter');
  if (value === 'none') return 'none';
  return value === 'area' ? 'area' : 'diameter';
}

function getCPRGraphMeta(branchdata: BranchInfo, branchId: number) {
  const metric = getSelectedCPRGraphMetric();
  if (metric === 'none') {
    return {
      metric,
      label: 'None',
      mean: 0,
    };
  }
  const stats = branchdata.getStats(branchId);
  if (metric === 'area') {
    return {
      metric,
      label: 'Area',
      mean: stats?.lumenArea?.mean ?? 0,
    };
  }
  return {
    metric,
    label: 'Diameter',
    mean: stats?.lumenDiameter?.mean ?? 0,
  };
}

function getCPRGraphSeries(
  dataset: BranchInfo,
  branchId: number,
): {
  metric: 'diameter' | 'area';
  values: number[];
  yMin: number;
  yMax: number;
} | null {
  const metric = getSelectedCPRGraphMetric();
  if (metric === 'none') return null;
  const raw =
    metric === 'area'
      ? dataset.getLumenArea(branchId)
      : dataset.getLumenDiameter(branchId);
  if (!raw || raw.length === 0) return null;
  const values = Array.from(raw).filter((v) => Number.isFinite(v));
  if (values.length < 2) return null;
  // Use a fixed graph domain so branch-to-branch captures keep identical
  // apparent width/scale regardless of local value range.
  const yMin = 0;
  const yMax = metric === 'area' ? 25 : 6;

  return { metric, values, yMin, yMax };
}

function createCPRGraphComposer(
  pool: BufferPoolItem,
  branchId?: number,
): ((cprUrl: string) => Promise<string>) | null {
  if (branchId === undefined || branchId === null) return null;
  const dataset = pool?.branchGraphDataset?.data as BranchInfo | undefined;
  const centerData = (pool?.centerData?.data as Record<number, vtkPolyData>)?.[
    branchId
  ];
  if (!dataset || !centerData) return null;

  const series = getCPRGraphSeries(dataset, branchId);
  if (!series) return null;
  const smoothedValues = movingAverage(series.values, 10);
  const frontExpand = Math.max(0, configStore.get().coronary_front_expand ?? 0);
  const backExpand = Math.max(0, configStore.get().coronary_back_expand ?? 0);
  const { coreStartRatio, coreEndRatio } = getCoreVerticalRatios(
    centerData,
    frontExpand,
    backExpand,
  );

  return (cprUrl: string) =>
    composeCPRWithDiameterGraph(cprUrl, {
      diameter: smoothedValues,
      yMin: series.yMin,
      yMax: series.yMax,
      coreStartRatio,
      coreEndRatio,
    });
}

async function composeCPRWithDiameterGraph(
  cprUrl: string,
  opts: {
    diameter: number[];
    yMin: number;
    yMax: number;
    coreStartRatio: number;
    coreEndRatio: number;
  },
): Promise<string> {
  const FIXED_GRAPH_WIDTH = 160;
  const FIXED_PLOT_WIDTH = 96;
  const CURVE_USAGE_RATIO = 0.62;
  const image = await loadImage(cprUrl);
  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;
  if (width <= 0 || height <= 0) return cprUrl;

  const graphWidth = FIXED_GRAPH_WIDTH;
  const gap = 0;
  const out = document.createElement('canvas');
  out.width = width + graphWidth + gap;
  out.height = height;
  const ctx = out.getContext('2d');
  if (!ctx) return cprUrl;

  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.drawImage(image, 0, 0, width, height);

  const graphX = width;
  const leftPad = 0;
  const rightPad = 14;
  const contentBounds = detectVerticalContentBounds(image);
  const topPad = contentBounds.top;
  const bottomPad = Math.max(0, height - contentBounds.bottom);
  const rawInnerX = graphX + leftPad + 1;
  const innerY = topPad;
  const rawInnerW = Math.max(20, graphWidth - leftPad - rightPad);
  const innerW = Math.min(rawInnerW, FIXED_PLOT_WIDTH);
  const innerX = rawInnerX + (rawInnerW - innerW) * 0.5;
  const innerH = Math.max(20, height - topPad - bottomPad);

  ctx.strokeStyle = '#6d6d6d';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(graphX, 0);
  ctx.lineTo(graphX, height);
  ctx.stroke();

  const coreStartRatio = Math.max(0, Math.min(1, opts.coreStartRatio));
  const coreEndRatio = Math.max(coreStartRatio, Math.min(1, opts.coreEndRatio));
  const coreTop = innerY + innerH * coreStartRatio;
  const coreBottom = innerY + innerH * coreEndRatio;
  const coreHeight = Math.max(2, coreBottom - coreTop);

  const domain = getZeroBasedCurveDomain(opts.diameter, opts.yMin, opts.yMax);
  const domainSpan = Math.max(1e-6, domain.max - domain.min);
  const curveW = Math.max(8, innerW * CURVE_USAGE_RATIO);
  // Anchor curve to CPR-right border (graph x-axis).
  const curveX0 = graphX + 1;
  const mappedX = opts.diameter.map((value) => {
    const xRatio = (value - domain.min) / domainSpan;
    return curveX0 + Math.max(0, Math.min(1, xRatio)) * curveW;
  });
  const minMappedX = mappedX.length ? Math.min(...mappedX) : curveX0;
  const anchoredX = mappedX.map((x) => x - minMappedX + curveX0);
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  opts.diameter.forEach((_, i) => {
    const t = opts.diameter.length === 1 ? 0 : i / (opts.diameter.length - 1);
    const y = coreTop + coreHeight * t;
    const x = anchoredX[i];
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.stroke();

  // ctx.fillStyle = '#d9d9d9';
  // ctx.font = '11px sans-serif';
  // ctx.textAlign = 'left';
  // const titleY = Math.max(12, innerY - 4);
  // ctx.fillText('Diameter', innerX, titleY);

  return out.toDataURL('image/png');
}

function detectVerticalContentBounds(image: HTMLImageElement): {
  top: number;
  bottom: number;
} {
  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;
  if (width <= 0 || height <= 0) return { top: 0, bottom: Math.max(1, height) };

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return { top: 0, bottom: Math.max(1, height) };
  ctx.drawImage(image, 0, 0, width, height);

  const { data } = ctx.getImageData(0, 0, width, height);
  const xStep = Math.max(1, Math.floor(width / 320));
  const yStep = 1;
  const blackThreshold = 12;
  const requiredRatio = 0.015;
  const requiredRun = 3;

  const rowHasContent = (y: number) => {
    let nonBlack = 0;
    let total = 0;
    for (let x = 0; x < width; x += xStep) {
      const i = (y * width + x) * 4;
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];
      if (
        a > 10 &&
        (r > blackThreshold || g > blackThreshold || b > blackThreshold)
      ) {
        nonBlack++;
      }
      total++;
    }
    return total > 0 && nonBlack / total >= requiredRatio;
  };

  let top = 0;
  let run = 0;
  for (let y = 0; y < height; y += yStep) {
    if (rowHasContent(y)) {
      run++;
      if (run >= requiredRun) {
        top = Math.max(0, y - (requiredRun - 1));
        break;
      }
    } else {
      run = 0;
    }
  }

  let bottom = height;
  run = 0;
  for (let y = height - 1; y >= 0; y -= yStep) {
    if (rowHasContent(y)) {
      run++;
      if (run >= requiredRun) {
        bottom = Math.min(height, y + requiredRun);
        break;
      }
    } else {
      run = 0;
    }
  }

  if (bottom <= top) return { top: 0, bottom: height };
  return { top, bottom };
}

function createCPRGraphHUComposer(
  pool: BufferPoolItem,
  branchId?: number,
): ((result: CPRExportResult) => CPRExportResult) | null {
  if (branchId === undefined || branchId === null) return null;
  const dataset = pool?.branchGraphDataset?.data as BranchInfo | undefined;
  const centerData = (pool?.centerData?.data as Record<number, vtkPolyData>)?.[
    branchId
  ];
  if (!dataset || !centerData) return null;

  const series = getCPRGraphSeries(dataset, branchId);
  if (!series) return null;
  const smoothedValues = movingAverage(series.values, 10);
  const frontExpand = Math.max(0, configStore.get().coronary_front_expand ?? 0);
  const backExpand = Math.max(0, configStore.get().coronary_back_expand ?? 0);
  const { coreStartRatio, coreEndRatio } = getCoreVerticalRatios(
    centerData,
    frontExpand,
    backExpand,
  );

  return (result: CPRExportResult) =>
    composeCPRHUWithDiameterGraph(result, {
      diameter: smoothedValues,
      yMin: series.yMin,
      yMax: series.yMax,
      coreStartRatio,
      coreEndRatio,
    });
}

function composeCPRHUWithDiameterGraph(
  result: CPRExportResult,
  opts: {
    diameter: number[];
    yMin: number;
    yMax: number;
    coreStartRatio: number;
    coreEndRatio: number;
  },
): CPRExportResult {
  const FIXED_GRAPH_WIDTH = 160;
  const FIXED_PLOT_WIDTH = 96;
  const CURVE_USAGE_RATIO = 0.62;
  const width = result.width;
  const height = result.height;
  if (width <= 0 || height <= 0 || opts.diameter.length < 2) return result;

  const minHU = Math.round(result.extent.minHU);
  // Use a fixed absolute HU for graph strokes so brightness does not vary
  // by per-image extent.
  const graphLineHU = 32767;
  const graphWidth = FIXED_GRAPH_WIDTH;
  const gap = 0;
  const newWidth = width + gap + graphWidth;

  const out = new Int16Array(newWidth * height).fill(minHU);
  for (let y = 0; y < height; y++) {
    const srcRow = y * width;
    const dstRow = y * newWidth;
    out.set(result.data.subarray(srcRow, srcRow + width), dstRow);
  }

  const graphX = width;
  const leftPad = 0;
  const rightPad = 14;
  const bounds = detectVerticalContentBoundsHU(result);
  const rawInnerX = graphX + leftPad + 1;
  const innerY = bounds.top;
  const rawInnerW = Math.max(20, graphWidth - leftPad - rightPad);
  const innerW = Math.min(rawInnerW, FIXED_PLOT_WIDTH);
  const innerX = rawInnerX + (rawInnerW - innerW) * 0.5;
  const innerH = Math.max(20, bounds.bottom - bounds.top);

  const coreStartRatio = Math.max(0, Math.min(1, opts.coreStartRatio));
  const coreEndRatio = Math.max(coreStartRatio, Math.min(1, opts.coreEndRatio));
  const coreTop = innerY + innerH * coreStartRatio;
  const coreBottom = innerY + innerH * coreEndRatio;
  const coreHeight = Math.max(2, coreBottom - coreTop);
  const domain = getZeroBasedCurveDomain(opts.diameter, opts.yMin, opts.yMax);
  const domainSpan = Math.max(1e-6, domain.max - domain.min);

  const sampleSeries = (arr: number[], t: number) => {
    if (arr.length <= 1) return arr[0] ?? 0;
    const pos = Math.max(0, Math.min(arr.length - 1, t * (arr.length - 1)));
    const i0 = Math.floor(pos);
    const i1 = Math.min(arr.length - 1, i0 + 1);
    const w = pos - i0;
    return arr[i0] * (1 - w) + arr[i1] * w;
  };

  const setPixel = (x: number, y: number, value: number) => {
    if (x < 0 || x >= newWidth || y < 0 || y >= height) return;
    out[y * newWidth + x] = value;
  };

  const drawSegment = (x0: number, y0: number, x1: number, y1: number) => {
    const dx = Math.abs(x1 - x0);
    const dy = Math.abs(y1 - y0);
    const sx = x0 < x1 ? 1 : -1;
    const sy = y0 < y1 ? 1 : -1;
    let err = dx - dy;
    let cx = x0;
    let cy = y0;
    while (true) {
      setPixel(cx, cy, graphLineHU);
      if (cx === x1 && cy === y1) break;
      const e2 = err * 2;
      if (e2 > -dy) {
        err -= dy;
        cx += sx;
      }
      if (e2 < dx) {
        err += dx;
        cy += sy;
      }
    }
  };

  const coreTopPx = Math.max(0, Math.min(height - 1, Math.round(coreTop)));
  const coreBottomPx = Math.max(coreTopPx + 1, Math.min(height, Math.round(coreBottom)));
  const corePixelHeight = Math.max(2, coreBottomPx - coreTopPx);
  const curveW = Math.max(8, innerW * CURVE_USAGE_RATIO);
  // Anchor curve to CPR-right border (graph x-axis).
  const curveX0 = graphX + 1;
  const sampledX: number[] = [];
  for (let i = 0; i < corePixelHeight; i++) {
    const t = corePixelHeight <= 1 ? 0 : i / (corePixelHeight - 1);
    const value = sampleSeries(opts.diameter, t);
    const xRatio = (value - domain.min) / domainSpan;
    const x = curveX0 + Math.max(0, Math.min(1, xRatio)) * (curveW - 1);
    sampledX.push(x);
  }
  const smoothedX = movingAverage(sampledX, 9);
  const minSmoothedX = smoothedX.length ? Math.min(...smoothedX) : curveX0;
  const anchoredSmoothedX = smoothedX.map((x) => x - minSmoothedX + curveX0);
  const points: Array<{ x: number; y: number }> = [];
  for (let i = 0; i < corePixelHeight; i++) {
    points.push({
      x: Math.round(anchoredSmoothedX[i]),
      y: coreTopPx + i,
    });
  }

  for (let i = 1; i < points.length; i++) {
    drawSegment(points[i - 1].x, points[i - 1].y, points[i].x, points[i].y);
  }

  for (let y = 0; y < height; y++) {
    setPixel(graphX, y, graphLineHU);
  }

  return {
    ...result,
    width: newWidth,
    data: out,
  };
}

function getZeroBasedCurveDomain(
  values: number[],
  baseMin: number,
  baseMax: number,
): { min: number; max: number } {
  const finite = values.filter((v) => Number.isFinite(v));
  if (finite.length < 2) return { min: baseMin, max: baseMax };
  const minObserved = Math.min(...finite);
  const maxObserved = Math.max(...finite);
  let min = Math.max(baseMin, minObserved);
  const spanObserved = Math.max(1e-6, maxObserved - minObserved);
  let max = Math.min(baseMax, maxObserved + spanObserved * 0.08);
  if (max - min < 1e-6) {
    min = baseMin;
    max = baseMax;
  }
  return { min, max };
}

function detectVerticalContentBoundsHU(result: CPRExportResult): {
  top: number;
  bottom: number;
} {
  const width = result.width;
  const height = result.height;
  if (width <= 0 || height <= 0) return { top: 0, bottom: height };

  const minHU = Math.round(result.extent.minHU);
  const threshold = minHU + 8;
  const xStep = Math.max(1, Math.floor(width / 300));
  const requiredRatio = 0.01;
  const requiredRun = 3;

  const rowHasContent = (y: number) => {
    let nonBg = 0;
    let total = 0;
    const row = y * width;
    for (let x = 0; x < width; x += xStep) {
      if (result.data[row + x] > threshold) nonBg++;
      total++;
    }
    return total > 0 && nonBg / total >= requiredRatio;
  };

  let top = 0;
  let run = 0;
  for (let y = 0; y < height; y++) {
    if (rowHasContent(y)) {
      run++;
      if (run >= requiredRun) {
        top = Math.max(0, y - (requiredRun - 1));
        break;
      }
    } else {
      run = 0;
    }
  }

  let bottom = height;
  run = 0;
  for (let y = height - 1; y >= 0; y--) {
    if (rowHasContent(y)) {
      run++;
      if (run >= requiredRun) {
        bottom = Math.min(height, y + requiredRun);
        break;
      }
    } else {
      run = 0;
    }
  }

  if (bottom <= top) return { top: 0, bottom: height };
  return { top, bottom };
}

function movingAverage(arr: ArrayLike<number>, window: number): number[] {
  if (window <= 1) return Array.from(arr);
  const out: number[] = [];
  const n = arr.length;
  const half = Math.floor(window / 2);
  for (let i = 0; i < n; ++i) {
    let sum = 0;
    let count = 0;
    for (let j = i - half; j <= i + half; ++j) {
      if (j >= 0 && j < n) {
        sum += arr[j];
        count++;
      }
    }
    out.push(count > 0 ? sum / count : (arr[i] ?? 0));
  }
  return out;
}

function getCoreVerticalRatios(
  centerData: vtkPolyData,
  frontExpand: number,
  backExpand: number,
): { coreStartRatio: number; coreEndRatio: number } {
  const points = centerData.getPoints()?.getData?.() ?? null;
  const count = points ? Math.floor(points.length / 3) : 0;
  if (!points || count < 2) return { coreStartRatio: 0, coreEndRatio: 1 };

  const cumulative = new Array<number>(count).fill(0);
  for (let i = 1; i < count; i++) {
    const x0 = points[(i - 1) * 3];
    const y0 = points[(i - 1) * 3 + 1];
    const z0 = points[(i - 1) * 3 + 2];
    const x1 = points[i * 3];
    const y1 = points[i * 3 + 1];
    const z1 = points[i * 3 + 2];
    const dx = x1 - x0;
    const dy = y1 - y0;
    const dz = z1 - z0;
    cumulative[i] = cumulative[i - 1] + Math.hypot(dx, dy, dz);
  }

  const totalLength = cumulative[count - 1];
  if (totalLength <= 1e-6) {
    const totalIndex = count - 1;
    const start = Math.max(0, Math.min(frontExpand, totalIndex));
    const end = Math.max(start, Math.min(totalIndex, totalIndex - backExpand));
    return {
      coreStartRatio: start / totalIndex,
      coreEndRatio: end / totalIndex,
    };
  }

  const startIdx = Math.max(0, Math.min(frontExpand, count - 1));
  const endIdx = Math.max(
    startIdx,
    Math.min(count - 1, count - 1 - backExpand),
  );
  return {
    coreStartRatio: cumulative[startIdx] / totalLength,
    coreEndRatio: cumulative[endIdx] / totalLength,
  };
}

export async function chamberCapture(
  type: AutoCaptureMode,
  poolRef: React.MutableRefObject<ContextPool>,
  panelStates: PanelStates,
  voi: VOI,
  selectedSeriesId: string,
  setWaitingProgressMessage: (
    value: React.SetStateAction<string | undefined>,
  ) => void,
) {
  let captureIds: string[] = [];
  switch (type) {
    case '2chamberCapture':
      captureIds.push('2chamberCapture');
      break;
    case '4chamberCapture':
      captureIds.push('4chamberCapture');
      break;
    case 'shortaxisCapture':
      captureIds.push('shortaxisCapture');
      break;
    case 'axialCapture':
      captureIds.push('axialCapture');
      break;
    case 'fullChamberCapture':
      captureIds = configStore.get().chamberCaptureOrder;
      break;
  }
  // const result :Record<string,Record<string,CaptureChamberProps>>={};
  const chamberresult = await captureIds.map(async (captureId, index) => {
    let viewportId: PanelComponentKeyType = 'Empty';
    switch (captureId) {
      case '2chamberCapture':
        viewportId = 'TwoChamber';
        break;
      case '4chamberCapture':
        viewportId = 'FourChamber';
        break;
      case 'shortaxisCapture':
        viewportId = 'ShortAxis';
        break;
      case 'axialCapture':
        viewportId = 'Axial';
        break;
    }
    return requestCaptureChamberPlane({
      viewportId,
      panelStates,
      voi,
      poolRef,
      seriesId: selectedSeriesId!,
      onProgress({ loaded, total, progress }) {
        if (loaded) setWaitingProgressMessage(`\nUploading`);
        else
          setWaitingProgressMessage(
            `\nCapturing ${viewportId} : \t${progress} / ${total}`,
          );
      },
    });
  });

  const res = await Promise.all(chamberresult);
  // console.log(
  //   res[0].scout.position
  //   ,res[0].scoutInfo.orientation);
  // return [];
  let datas = [];
  for (const rec of res) {
    let buffers: BufferData[] = [];
    let desname: string = '';
    const { common, images, scout, scoutInfo } = rec;
    buffers.push({
      buffer: scout.plane.slice().buffer,
      width: scoutInfo.width,
      height: scoutInfo.height,
      extension: 'bin',
      orientation: scoutInfo.orientation,
      position: scout.position,
    });
    const sortedKeys = Object.keys(images).sort((a, b) => {
      return Number(a) > Number(b) ? 1 : -1;
    });
    const sortedImages = sortedKeys.reduce(
      (obj, key) => {
        obj[key] = images[key];
        return obj;
      },
      {} as typeof images,
    );
    // console.log(sortedImages);
    for (const [key, val] of Object.entries(sortedImages)) {
      // console.log(key)
      let buffer: BufferData = {
        buffer: val.plane.slice().buffer,
        width: common.width,
        height: common.height,
        extension: 'bin',
        orientation: common.orientation,
        position: val.position,
      };
      buffers.push(buffer);
      if (common.label === 'Axial') {
        desname = 'Axial';
      } else if (common.label === 'FourChamber') {
        desname = '4-chamber';
      } else if (common.label === 'TwoChamber') {
        desname = '2-chamber';
      } else if (common.label === 'ShortAxis') {
        desname = 'Short axis';
      } else {
        desname = common.label;
      }
    }

    const data = {
      buffers: buffers,
      screen_capture: false,
      capture_kind: 'chamber',
      series_description: desname,
      windowWidth: String(common.windowWidth),
      windowCenter: String(common.windowLevel),
      thickness: common.sliceThickness, //이거 스카웃 따로 해야하는디
    };
    datas.push(data);
  }

  return datas;
}
export async function capture360View(
  d3context: React.MutableRefObject<VRContext | SurfaceContext | null>,
  rotatedWorld?: [number, number, number],
) {
  const urls: string[] = [];
  let fir = await captureView(
    {
      type: 'vtk',
      renderWindow: d3context.current!.renderWindow,
    },
    { scale: 1 },
  );
  urls.push(fir);

  for (let i = 0; i < configStore.get().modelCaptureCountH; i++) {
    rotateByDegreesAtCenter(
      d3context,
      'azimuth',
      configStore.get().modelCaptureAngleH,
      rotatedWorld ?? d3context?.current?.actorsCenter,
    );
    if (i !== 11) {
      let url = await captureView(
        {
          type: 'vtk',
          renderWindow: d3context.current!.renderWindow,
        },
        { scale: 1 },
      );
      urls.push(url);
    }
  }

  for (let i = 0; i < configStore.get().modelCaptureCountV; i++) {
    rotateByDegreesAtCenter(
      d3context,
      'elevation',
      configStore.get().modelCaptureAngleV,
      rotatedWorld ?? d3context?.current?.actorsCenter,
    );
    let url = await captureView(
      {
        type: 'vtk',
        renderWindow: d3context.current!.renderWindow,
      },
      { scale: 1 },
    );
    urls.push(url);
  }
  return urls;
}

// export async function singleLowCaptureVR(
//   context: React.MutableRefObject<VRContext | null>,
//   bufferPool: BufferPool,
//   seriesId: string,
//   analysisType: AnalysisType,
//   currentVOI: VOI,
//   name: string,
//   visible3DKeys: string[],
//   rotatedWorld?: [number, number, number],
// ): Promise<Record<string, string[]>> {
//   const { windowLevel, windowWidth, invert } = currentVOI;

//   const urls: Record<string, string[]> = {};
//   if (!context.current) {
//     return urls;
//   }

//   const quality = configStore.get().generalSetting.renderingQuality;
//   const { renderer, actorIds, renderWindow } = context.current;

//   const backupActorIds = { ...context.current.actorIds };
//   const volumeDataBackups: any[] = [];
//   for (const [key, actor] of Object.entries(actorIds)) {
//     if ((actor as any).isA?.('vtkVolume')) {
//       const vol = actor as vtkVolume;
//       const mapper = vol.getMapper?.();
//       const imageData = mapper?.getInputData?.();
//       if (!imageData) continue;
//       volumeDataBackups.push({
//         key,
//         scalarsBackup: imageData.getPointData().getScalars().getData(),
//       });
//     }
//   }

//   context.current.actorIds = {};
//   const fit = configStore.get().modelCaptureFit;
//   const oriCamera = getOriCamera(renderer);

//   renderer.removeAllVolumes();
//   await runSegmentationVolume(
//     context,
//     bufferPool[seriesId],
//     seriesId!,
//     analysisType,
//     {
//       chamberEnabled: analysisType === 'chamber',
//       heartCut: configStore.get().heartCut,
//       visibleMIP: configStore.get().visibleMIP,
//       ww: windowWidth,
//       wl: windowLevel,
//       visibleKeys: visible3DKeys,
//       negativeMode: invert,
//       renderingQuality: quality,
//       useOriginalGeometry: quality === 'low',
//     },
//   );
//   context.current.actorIds[name]?.setVisibility(true);
//   applyScaledCameraPan(renderer, renderWindow, oriCamera, !fit);
//   urls['current'] = await capture360View(context, rotatedWorld);

//   context.current.actorIds = backupActorIds;
//   renderer.removeAllVolumes();
//   for (const [key, actor] of Object.entries(backupActorIds)) {
//     if ((actor as any).isA?.('vtkVolume')) {
//       const volume = actor as vtkVolume;
//       volume.setVisibility(key === name);
//       const volumeBackup = volumeDataBackups.find((x) => x.key === key);
//       const volumeMapper = volume.getMapper?.();
//       const imageData = volumeMapper?.getInputData?.();
//       if (!imageData || !volumeMapper || !volumeBackup) continue;
//       const data = volumeBackup.scalarsBackup;
//       const pointData = imageData.getPointData();
//       pointData?.getScalars()?.setData(data);
//       volumeMapper.setInputData(imageData);
//       volumeMapper.modified();
//       volume.modified();
//       renderer.addVolume(volume);
//     }
//   }
//   setPickingForVolume(context, analysisType);
//   return urls;
// }

// export async function captureVR(
//   context: React.MutableRefObject<VRContext | null>,
//   bufferPool: BufferPool,
//   seriesId: string,
//   analysisType: AnalysisType,
//   currentVOI: VOI,
//   name: string,
//   rotatedWorld?: [number, number, number],
// ): Promise<Record<string, string[]>> {
//   const { windowLevel, windowWidth, invert } = currentVOI;

//   const urls: Record<string, string[]> = {};
//   if (!context.current) {
//     return urls;
//   }

//   const { renderer, actorIds } = context.current;

//   const backupActorIds = { ...context.current.actorIds };
//   const volumeDataBackups: any[] = [];
//   for (const [key, actor] of Object.entries(actorIds)) {
//     if ((actor as any).isA?.('vtkVolume')) {
//       const vol = actor as vtkVolume;
//       const mapper = vol.getMapper?.();
//       const imageData = mapper?.getInputData?.();
//       if (!imageData) continue;
//       volumeDataBackups.push({
//         key,
//         scalarsBackup: imageData.getPointData().getScalars().getData(),
//       });
//     }
//   }

//   console.time('VR Capture time');
//   const captureMap: Record<string, string[]> = {};

//   const modelCaptureVR = configStore.get().modelCaptureVR;
//   console.log(modelCaptureVR);
//   const segNames = getSegmentationAnalysisType(analysisType);
//   if (modelCaptureVR['current'].capture) {
//     captureMap['current'] = await capture360View(context, rotatedWorld);
//   }

//   for (const [key, item] of Object.entries(modelCaptureVR)) {
//     const typedKey = key as keyof typeof modelCaptureVR;
//     if (['ventricle', 'coronaryAorta'].includes(typedKey)) {
//       const offSegs =
//         typedKey === 'ventricle'
//           ? ['la', 'laWall', 'ra', 'raWall', 'epicardial']
//           : [
//               'lv',
//               'lvWall',
//               'rv',
//               'rvWall',
//               'la',
//               'laWall',
//               'ra',
//               'raWall',
//               'epicardial',
//             ];
//       const visibleKeys = segNames.filter((n) => !offSegs.includes(n));
//       await runSegmentationVolume(
//         context,
//         bufferPool[seriesId],
//         seriesId!,
//         analysisType,
//         {
//           chamberEnabled: false,
//           heartCut: key === 'ventricle',
//           visibleMIP: key === 'ventricle',
//           ww: windowWidth,
//           wl: windowLevel,
//           visibleKeys,
//           negativeMode: invert,
//           renderingQuality: configStore.get().generalSetting.renderingQuality,
//         },
//       );
//       const volumes = context?.current?.renderer.getVolumes();
//       if (!volumes || volumes.length === 0) continue;
//       for (const v of volumes) {
//         v.setVisibility(false);
//       }

//       if (item.capture) {
//         actorIds['volume']?.setVisibility(true);
//         captureMap[`${key}`] = await capture360View(context, rotatedWorld);
//       }

//       if (key === 'ventricle') {
//         if (modelCaptureVR.ventricleHeartCut.capture) {
//           actorIds['volume']?.setVisibility(false);
//           actorIds['volumeMIP']?.setVisibility(false);
//           actorIds['volumeHeartCut']?.setVisibility(true);
//           captureMap[`${key}HeartCut`] = await capture360View(
//             context,
//             rotatedWorld,
//           );
//         }
//         if (modelCaptureVR.MIP.capture) {
//           actorIds['volume']?.setVisibility(false);
//           actorIds['volumeHeartCut']?.setVisibility(false);
//           actorIds['volumeMIP']?.setVisibility(true);
//           captureMap[`MIP`] = await capture360View(context, rotatedWorld);
//         }
//       }
//     }
//   }
//   console.log(captureMap);
//   for (const [key, item] of Object.entries(modelCaptureVR)) {
//     if (captureMap[key]) {
//       urls[`${key}`] = captureMap[`${key}`];
//     }
//   }

//   context.current.actorIds = backupActorIds;
//   renderer.removeAllVolumes();
//   for (const [key, actor] of Object.entries(backupActorIds)) {
//     if ((actor as any).isA?.('vtkVolume')) {
//       const volume = actor as vtkVolume;
//       volume.setVisibility(key === name);
//       const volumeBackup = volumeDataBackups.find((x) => x.key === key);
//       const volumeMapper = volume.getMapper?.();
//       const imageData = volumeMapper?.getInputData?.();
//       if (!imageData || !volumeMapper || !volumeBackup) continue;
//       const data = volumeBackup.scalarsBackup;
//       const pointData = imageData.getPointData();
//       pointData?.getScalars()?.setData(data);
//       volumeMapper.setInputData(imageData);
//       volumeMapper.modified();
//       volume.modified();
//       renderer.addVolume(volume);
//     }
//   }

//   setPickingForVolume(context, analysisType);

//   console.timeEnd('VR Capture time');
//   return urls;
// }

// function getOriCamera(renderer: vtkRenderer) {
//   const camera = renderer.getActiveCamera()!;
//   return {
//     position: camera.getPosition().slice() as [number, number, number],
//     focalPoint: camera.getFocalPoint().slice() as [number, number, number],
//     viewUp: camera.getViewUp().slice() as [number, number, number],
//     parallelScale: camera.getParallelScale(),
//   };
// }

// function applyScaledCameraPan(
//   renderer: vtkRenderer,
//   renderWindow: vtkRenderWindow,
//   oriCamera: {
//     position: [number, number, number];
//     focalPoint: [number, number, number];
//     viewUp: [number, number, number];
//     parallelScale: number;
//   },
//   apply: boolean = true,
// ) {
//   if (!apply) return;
//   console.log('이전위치싹.');
//   const camera: vtkCamera = renderer.getActiveCamera()!;
//   camera.setPosition(...oriCamera.position);
//   camera.setFocalPoint(...oriCamera.focalPoint);
//   camera.setViewUp(oriCamera.viewUp);
//   camera.setParallelScale(oriCamera.parallelScale);
//   renderer.resetCameraClippingRange();
//   renderer.updateLightsGeometryToFollowCamera();
//   renderWindow.render();
// }

// export async function LowCaptureVR(
//   context: React.MutableRefObject<VRContext | null>,
//   bufferPool: BufferPool,
//   seriesId: string,
//   analysisType: AnalysisType,
//   currentVOI: VOI,
//   name: string,
//   visible3DKeys: string[],
//   rotatedWorld?: [number, number, number],
// ): Promise<Record<string, string[]>> {
//   const { windowLevel, windowWidth, invert } = currentVOI;

//   const urls: Record<string, string[]> = {};
//   if (!context.current) {
//     return urls;
//   }

//   const { renderer, actorIds, renderWindow, savedTranslation } =
//     context.current;

//   const fit = configStore.get().modelCaptureFit;
//   const oriCamera = getOriCamera(renderer);

//   const backupActorIds = { ...context.current.actorIds };
//   const volumeDataBackups: any[] = [];
//   for (const [key, actor] of Object.entries(actorIds)) {
//     if ((actor as any).isA?.('vtkVolume')) {
//       const vol = actor as vtkVolume;
//       const mapper = vol.getMapper?.();
//       const imageData = mapper?.getInputData?.();
//       if (!imageData) continue;
//       volumeDataBackups.push({
//         key,
//         scalarsBackup: imageData.getPointData().getScalars().getData(),
//       });
//     }
//   }

//   console.time('VR Capture time');
//   const captureMap: Record<string, string[]> = {};
//   const quality = configStore.get().generalSetting.renderingQuality;
//   context.current.actorIds = {};
//   renderer.removeAllVolumes();

//   const modelCaptureVR = configStore.get().modelCaptureVR;
//   console.log(modelCaptureVR);
//   const segNames = getSegmentationAnalysisType(analysisType);
//   if (modelCaptureVR['current'].capture) {
//     await runSegmentationVolume(
//       context,
//       bufferPool[seriesId],
//       seriesId!,
//       analysisType,
//       {
//         chamberEnabled: analysisType === 'chamber',
//         heartCut: configStore.get().heartCut,
//         visibleMIP: configStore.get().visibleMIP,
//         ww: windowWidth,
//         wl: windowLevel,
//         visibleKeys: visible3DKeys,
//         negativeMode: invert,
//         renderingQuality: quality,
//         useOriginalGeometry: quality === 'low',
//       },
//     );
//     context.current.actorIds[name]?.setVisibility(true);

//     applyScaledCameraPan(renderer, renderWindow, oriCamera, !fit);
//     urls['current'] = await capture360View(context, rotatedWorld);
//   }

//   for (const [key, item] of Object.entries(modelCaptureVR)) {
//     const typedKey = key as keyof typeof modelCaptureVR;
//     if (['ventricle', 'coronaryAorta'].includes(typedKey)) {
//       const offSegs =
//         typedKey === 'ventricle'
//           ? ['la', 'laWall', 'ra', 'raWall', 'epicardial']
//           : [
//               'lv',
//               'lvWall',
//               'rv',
//               'rvWall',
//               'la',
//               'laWall',
//               'ra',
//               'raWall',
//               'epicardial',
//             ];
//       const visibleKeys = segNames.filter((n) => !offSegs.includes(n));
//       await runSegmentationVolume(
//         context,
//         bufferPool[seriesId],
//         seriesId!,
//         analysisType,
//         {
//           chamberEnabled: false,
//           heartCut: key === 'ventricle',
//           visibleMIP: key === 'ventricle',
//           ww: windowWidth,
//           wl: windowLevel,
//           visibleKeys,
//           negativeMode: invert,
//           renderingQuality: quality,
//           useOriginalGeometry: quality === 'low',
//         },
//       );
//       const volumes = context?.current?.renderer.getVolumes();
//       if (!volumes || volumes.length === 0) continue;
//       for (const v of volumes) {
//         v.setVisibility(false);
//       }

//       applyScaledCameraPan(renderer, renderWindow, oriCamera, !fit);

//       if (item.capture) {
//         context.current.actorIds['volume']?.setVisibility(true);
//         captureMap[`${key}`] = await capture360View(context, rotatedWorld);
//       }

//       if (key === 'ventricle') {
//         if (modelCaptureVR.ventricleHeartCut.capture) {
//           context.current.actorIds['volume']?.setVisibility(false);
//           context.current.actorIds['volumeMIP']?.setVisibility(false);
//           context.current.actorIds['volumeHeartCut']?.setVisibility(true);
//           captureMap[`${key}HeartCut`] = await capture360View(
//             context,
//             rotatedWorld,
//           );
//         }
//         if (modelCaptureVR.MIP.capture) {
//           context.current.actorIds['volume']?.setVisibility(false);
//           context.current.actorIds['volumeHeartCut']?.setVisibility(false);
//           context.current.actorIds['volumeMIP']?.setVisibility(true);
//           captureMap[`MIP`] = await capture360View(context, rotatedWorld);
//         }
//       }
//     }
//   }
//   console.log(captureMap);
//   for (const [key, item] of Object.entries(modelCaptureVR)) {
//     if (captureMap[key]) {
//       urls[`${key}`] = captureMap[`${key}`];
//     }
//   }

//   context.current.actorIds = backupActorIds;
//   renderer.removeAllVolumes();
//   for (const [key, actor] of Object.entries(backupActorIds)) {
//     if ((actor as any).isA?.('vtkVolume')) {
//       const volume = actor as vtkVolume;
//       volume.setVisibility(key === name);
//       const volumeBackup = volumeDataBackups.find((x) => x.key === key);
//       const volumeMapper = volume.getMapper?.();
//       const imageData = volumeMapper?.getInputData?.();
//       if (!imageData || !volumeMapper || !volumeBackup) continue;
//       const data = volumeBackup.scalarsBackup;
//       const pointData = imageData.getPointData();
//       pointData?.getScalars()?.setData(data);
//       volumeMapper.setInputData(imageData);
//       volumeMapper.modified();
//       volume.modified();
//       renderer.addVolume(volume);
//     }
//   }
//   setPickingForVolume(context, analysisType);
//   console.timeEnd('VR Capture time');
//   return urls;
// }

// export async function modelCapture(
//   type: AutoCaptureMode,
//   poolRef: React.MutableRefObject<ContextPool>,
//   currentVOI: VOI,
//   bufferPool: BufferPool,
//   seriesId: string,
//   analysisType: AnalysisType,
//   single: boolean,
//   visible3DKeys: string[],
// ) {
//   // config 또는 인자로 받는 플래그
//   const fit = configStore.get().modelCaptureFit;

//   if (fit) {
//     return await modelCaptureFit(
//       type,
//       poolRef,
//       currentVOI,
//       bufferPool,
//       seriesId,
//       analysisType,
//       single,
//       visible3DKeys,
//     );
//   } else {
//     return await modelCaptureCustom(
//       type,
//       poolRef,
//       currentVOI,
//       bufferPool,
//       seriesId,
//       analysisType,
//       single,
//       visible3DKeys,
//     );
//   }
// }

// export async function modelCaptureCustom(
//   type: AutoCaptureMode,
//   poolRef: React.MutableRefObject<ContextPool>,
//   currentVOI: VOI,
//   bufferPool: BufferPool,
//   seriesId: string,
//   analysisType: AnalysisType,
//   single: boolean,
//   visible3DKeys: string[],
// ) {
//   const keyname = type === 'Surface3DCapture' ? `Surface3D` : 'VR';
//   const d3context = {
//     current:
//       keyname === 'VR'
//         ? (poolRef.current['VR'] as VRContext)
//         : (poolRef.current['Surface3D'] as SurfaceContext),
//   } as React.MutableRefObject<VRContext | SurfaceContext | null>;
//   if (!d3context) return;
//   if (!d3context.current) return;

//   const {
//     genericRenderWindow,
//     renderer,
//     renderWindow,
//     orientationWidget,
//     isAxis3DVisible,
//   } = d3context.current;

//   const ref = genericRenderWindow.getContainer();

//   setNewContainer(
//     genericRenderWindow,
//     configStore.get().captureWidth,
//     configStore.get().captureHeight,
//   );

//   //캡처시 3d axis 비활성화
//   setAxesLinesVisibility({ context: d3context, visible: false });
//   //setActorsCenter(d3context); //리턴없이 d3context 내부 변수로 스윽 set

//   const oriCamera = getOriCamera(renderer);
//   renderWindow.render();
//   let name = configStore.get().visibleMIP
//     ? 'volumeMIP'
//     : configStore.get().heartCut
//       ? 'volumeHeartCut'
//       : 'volume';
//   if (keyname === 'Surface3D') {
//     name = 'epicardial';
//   }

//   let allUrls: Record<string, string[]> = {};

//   let world: [number, number, number] | undefined;

//   const { focus, savedTranslation, actorsCenter } = d3context.current;
//   if (focus) {
//     const [x, y, z] = focus;
//     world = [-x, y, z];
//   }
//   console.log('actorsCenter', d3context?.current?.actorsCenter, focus);

//   if (keyname === 'VR') {
//     if (single) {
//       if (configStore.get().generalSetting.renderingQuality !== 'low') {
//         allUrls['current'] = await capture360View(d3context, world);
//       } else {
//         allUrls = await singleLowCaptureVR(
//           d3context as React.MutableRefObject<VRContext | null>,
//           bufferPool,
//           seriesId,
//           analysisType,
//           currentVOI,
//           name,
//           visible3DKeys,
//           world,
//         );
//       }
//     } else {
//       if (configStore.get().generalSetting.renderingQuality !== 'low') {
//         allUrls = await captureVR(
//           d3context as React.MutableRefObject<VRContext | null>,
//           bufferPool,
//           seriesId,
//           analysisType,
//           currentVOI,
//           name,
//           world,
//         );
//       } else {
//         allUrls = await LowCaptureVR(
//           d3context as React.MutableRefObject<VRContext | null>,
//           bufferPool,
//           seriesId,
//           analysisType,
//           currentVOI,
//           name,
//           visible3DKeys,
//           world,
//         );
//       }
//     }
//   } else if (keyname === 'Surface3D') {
//     allUrls['current'] = await capture360View(d3context, world);
//   }
//   d3context.current.actorsCenter = actorsCenter;
//   d3context.current.focus = focus;
//   d3context.current.savedTranslation = savedTranslation;

//   if (ref) genericRenderWindow.setContainer(ref);

//   //이전 위치로 싸악 이동
//   orientationWidget?.setEnabled(true);
//   applyScaledCameraPan(renderer, renderWindow, oriCamera);

//   setAxesLinesVisibility({
//     context: d3context,
//     visible: isAxis3DVisible,
//   });

//   genericRenderWindow.resize();
//   renderWindow.render();

//   let datas = [];
//   let buffers: BufferData[] = [];
//   const modelCaptureVR = configStore.get().modelCaptureVR;
//   for (const [key, urlList] of Object.entries(allUrls)) {
//     for (const url of urlList) {
//       const buffer = await imageUrlToArrayBufferSimple(url);
//       buffers.push(buffer);
//     }
//     const captureInfo = modelCaptureVR[key as keyof typeof modelCaptureVR];
//     const data = {
//       buffers: buffers,
//       screen_capture: false,
//       capture_kind: '3D',
//       series_description: captureInfo.description,
//     };
//     datas.push(data);
//     buffers = [];
//   }

//   return datas;
// }

// export async function modelCaptureFit(
//   type: AutoCaptureMode,
//   poolRef: React.MutableRefObject<ContextPool>,
//   currentVOI: VOI,
//   bufferPool: BufferPool,
//   seriesId: string,
//   analysisType: AnalysisType,
//   single: boolean,
//   visible3DKeys: string[],
// ) {
//   const keyname = type === 'Surface3DCapture' ? `Surface3D` : 'VR';
//   const d3context = {
//     current:
//       keyname === 'VR'
//         ? (poolRef.current['VR'] as VRContext)
//         : (poolRef.current['Surface3D'] as SurfaceContext),
//   } as React.MutableRefObject<VRContext | SurfaceContext | null>;
//   if (!d3context) return;
//   if (!d3context.current) return;

//   const {
//     genericRenderWindow,
//     renderer,
//     actorIds,
//     canvas,
//     renderWindow,
//     orientationWidget,
//     isAxis3DVisible,
//     focus,
//     savedTranslation,
//     actorsCenter,
//   } = d3context.current;

//   console.log('actorsCenter', d3context?.current?.actorsCenter, focus);
//   let name = configStore.get().visibleMIP
//     ? 'volumeMIP'
//     : configStore.get().heartCut
//       ? 'volumeHeartCut'
//       : 'volume';
//   if (keyname === 'Surface3D') {
//     name = 'epicardial';
//   }
//   if (!actorIds[name]) return;

//   const ref = genericRenderWindow.getContainer();

//   setNewContainer(
//     genericRenderWindow,
//     configStore.get().captureWidth,
//     configStore.get().captureHeight,
//   );

//   //캡처시 3d axis 비활성화
//   setAxesLinesVisibility({ context: d3context, visible: false });
//   //setActorsCenter(d3context); //리턴없이 d3context 내부 변수로 스윽 set

//   orientationWidget?.setEnabled(false);
//   const oriCamera = getOriCamera(renderer);
//   renderer.resetCamera();
//   renderWindow.render();

//   fitActorToCanvas(renderer, actorIds[name].getBounds(), canvas);

//   let allUrls: Record<string, string[]> = {};

//   if (keyname === 'VR') {
//     if (single) {
//       if (configStore.get().generalSetting.renderingQuality !== 'low') {
//         allUrls['current'] = await capture360View(d3context, actorsCenter);
//       } else {
//         allUrls = await singleLowCaptureVR(
//           d3context as React.MutableRefObject<VRContext | null>,
//           bufferPool,
//           seriesId,
//           analysisType,
//           currentVOI,
//           name,
//           visible3DKeys,
//           actorsCenter,
//         );
//       }
//     } else {
//       if (configStore.get().generalSetting.renderingQuality !== 'low') {
//         allUrls = await captureVR(
//           d3context as React.MutableRefObject<VRContext | null>,
//           bufferPool,
//           seriesId,
//           analysisType,
//           currentVOI,
//           name,
//           actorsCenter,
//         );
//       } else {
//         allUrls = await LowCaptureVR(
//           d3context as React.MutableRefObject<VRContext | null>,
//           bufferPool,
//           seriesId,
//           analysisType,
//           currentVOI,
//           name,
//           visible3DKeys,
//           actorsCenter,
//         );
//       }
//     }
//   } else if (keyname === 'Surface3D') {
//     allUrls['current'] = await capture360View(d3context);
//   }

//   if (ref) genericRenderWindow.setContainer(ref);

//   d3context.current.actorsCenter = actorsCenter;
//   d3context.current.focus = focus;
//   d3context.current.savedTranslation = savedTranslation;

//   //이전 위치로 싸악 이동
//   orientationWidget?.setEnabled(true);
//   applyScaledCameraPan(renderer, renderWindow, oriCamera);

//   setAxesLinesVisibility({
//     context: d3context,
//     visible: isAxis3DVisible,
//   });

//   genericRenderWindow.resize();
//   renderWindow.render();

//   let datas = [];
//   let buffers: BufferData[] = [];
//   const modelCaptureVR = configStore.get().modelCaptureVR;
//   for (const [key, urlList] of Object.entries(allUrls)) {
//     for (const url of urlList) {
//       const buffer = await imageUrlToArrayBufferSimple(url);
//       buffers.push(buffer);
//     }
//     const captureInfo = modelCaptureVR[key as keyof typeof modelCaptureVR];
//     const data = {
//       buffers: buffers,
//       screen_capture: false,
//       capture_kind: '3D',
//       series_description: captureInfo.description,
//     };
//     datas.push(data);
//     buffers = [];
//   }
//   return datas;
// }

export function alignFunction(
  base: [number, number, number],
  target: [number, number, number],
  align: [number, number, number],
) {
  let res: ReturnType<typeof alignAxis> = {
    up: base,
    theta: 0,
  };
  // const align :[number, number, number]= key !== 'Axial'?[0,0,-1]:[0, 1, 0];
  res = alignAxis(base, target, align);
  return { axis: res.up, theta: res.theta };
}
export interface CaptureChamberImageProps {
  plane: Int16Array;
  position: [number, number, number];
  img?: string;
}
export interface CaptureChamberCommonProps {
  width: number;
  height: number;
  orientation: [number, number, number, number, number, number];
  windowLevel: number;
  windowWidth: number;
  sliceThickness: number;
  label: string;
}

function correctCenter(
  flippedCenter: [number, number, number],
  direction: [
    number,
    number,
    number,
    number,
    number,
    number,
    number,
    number,
    number,
  ],
  origin: [number, number, number],
  spacing: [number, number, number],
): [number, number, number] {
  // 1. world -> voxel index
  const relative: [number, number, number] = [
    (flippedCenter[0] - origin[0]) / spacing[0],
    (flippedCenter[1] - origin[1]) / spacing[1],
    (flippedCenter[2] - origin[2]) / spacing[2],
  ];

  const idx: [number, number, number] = [
    direction[0] * relative[0] +
      direction[1] * relative[1] +
      direction[2] * relative[2],
    direction[3] * relative[0] +
      direction[4] * relative[1] +
      direction[5] * relative[2],
    direction[6] * relative[0] +
      direction[7] * relative[1] +
      direction[8] * relative[2],
  ];

  // 2. voxel index -> 정상 world (direction = I)
  const corrected: [number, number, number] = [
    origin[0] + idx[0] * spacing[0],
    origin[1] + idx[1] * spacing[1],
    origin[2] + idx[2] * spacing[2],
  ];

  return corrected;
}

export async function requestCaptureChamberPlane({
  viewportId,
  seriesId,
  panelStates,
  voi,
  poolRef,
  imgURL,
  onProgress,
}: {
  viewportId: PanelComponentKeyType;
  seriesId: string;
  panelStates: PanelStates;
  voi: VOI;
  poolRef: React.MutableRefObject<ContextPool>;
  imgURL?: boolean;
  onProgress?: ({
    loaded,
    total,
    progress,
  }: {
    loaded: boolean;
    total?: number;
    progress?: number;
  }) => void;
}) {
  const panelState = panelStates[viewportId];
  if (!panelState) throw '없';
  const { chamberViewProps, thickness, thicknessMethod } = panelState;
  const { windowLevel, windowWidth } = voi;
  if (
    !chamberViewProps ||
    !thickness ||
    !thicknessMethod ||
    !windowLevel ||
    !windowWidth
  )
    throw `없? ${chamberViewProps} ${thickness} ${thicknessMethod} ${windowLevel} ${windowWidth}`;

  const highQuality =
    configStore.get().generalSetting.renderingQuality === 'high';

  console.time('스칼라는 오래걸리나?'); //스칼라는 오래걸리나?: 54.10888671875 ms
  const volume = getVolumeFromSeriesId(seriesId, !highQuality);
  if (!volume) throw '없??';

  //원본
  const scalar = highQuality
    ? gecalarDataFromVolume(volume)
    : poolRef.current.VR?.sharedContext.oriHUData
        .getPointData()
        .getScalars()
        .getData();
  console.timeEnd('스칼라는 오래걸리나?');

  if (!scalar) throw '없???';

  const spacing = volume.spacing.map(
    (v) => v * (highQuality ? 1 : 0.5),
  ) as typeof volume.spacing;

  const dimension = highQuality
    ? volume.dimensions
    : poolRef.current.VR?.sharedContext.oriHUData.getDimensions();

  if (!dimension) throw '없????';

  const smallest = volume.additionalDetails!['HUMin'] as number;
  const largest = volume.additionalDetails!['HUMax'] as number;
  const { centers, position } = chamberViewProps;
  if (!centers || centers.length === 0 || !position) throw '센터 없??';

  //guide 범위를 찾아낸다.
  const axis = getOrthoNormal(
    chamberViewProps.normal!,
    chamberViewProps.viewNormal!,
  );

  const orthoAxis = scalePoint3(axis, spacing);
  const step = (chamberViewProps.imageSize! * 0.5) / length(orthoAxis);

  const zAxis = normalize(chamberViewProps.guidePlaneNormal!);
  const yAxis = normalize(chamberViewProps.guideUp!);
  const xAxis = normalize(cross(yAxis, zAxis));

  const origin = position;

  const projectToPlane = (p: typeof axis): typeof axis => {
    const d = [
      p[0] - origin[0],
      p[1] - origin[1],
      p[2] - origin[2],
    ] as typeof p;
    const dotdn = dot(d, zAxis);
    const nn = [
      zAxis[0] * dotdn,
      zAxis[1] * dotdn,
      zAxis[2] * dotdn,
    ] as typeof p;
    return [p[0] - nn[0], p[1] - nn[1], p[2] - nn[2]];
  };

  const linePoints = centers.map((c) => {
    const p0 = projectToPlane(
      c.map((v, i) => v + orthoAxis[i] * step) as typeof axis,
    );
    const p1 = projectToPlane(
      c.map((v, i) => v - orthoAxis[i] * step) as typeof axis,
    );
    const r0 = [
      p0[0] - origin[0],
      p0[1] - origin[1],
      p0[2] - origin[2],
    ] as typeof axis;
    const r1 = [
      p1[0] - origin[0],
      p1[1] - origin[1],
      p1[2] - origin[2],
    ] as typeof axis;
    return [
      [dot(r0, xAxis), -dot(r0, yAxis)],
      [dot(r1, xAxis), -dot(r1, yAxis)],
    ] as [[number, number], [number, number]];
  });

  const xMin = Math.min(...linePoints.flat().map((v) => v[0]));
  const xMax = Math.max(...linePoints.flat().map((v) => v[0]));
  const yMin = Math.min(...linePoints.flat().map((v) => v[1]));
  const yMax = Math.max(...linePoints.flat().map((v) => v[1]));
  const imageSize = Math.ceil(Math.max(xMax - xMin, yMax - yMin));
  // console.log(linePoints,xMin,xMax,yMin,yMax,imageSize);
  const scoutThickness = length(
    scalePoint3(chamberViewProps.guidePlaneNormal!, spacing),
  );
  const res1 = await captureChamberPlane({
    viewportId,
    chamberViewProps: {
      ...chamberViewProps,
      centers: [origin],
      normal: chamberViewProps.guideUp,
      viewNormal: chamberViewProps.guidePlaneNormal,
      rollRad: 0,
      flipVertical: false,
      flipHorizontal: false,
      imageSize,
    },
    thickness: scoutThickness,
    thicknessMethod,
    origin: volume.origin,
    pixelSpacing: spacing,
    scalarArray: Int16Array.from(scalar),
    dimension,
    windowLevel,
    windowWidth,
    // largest,
    smallest,
    img: imgURL,
    onProgress: undefined,
    isScout: true,
    isHalf: false,
  });

  const drawLine = (
    img: Int16Array,
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    wh: number,
    dashGap: number,
  ) => {
    let dx = Math.abs(x1 - x0),
      sx = x0 < x1 ? 1 : -1;
    let dy = -Math.abs(y1 - y0),
      sy = y0 < y1 ? 1 : -1;
    let err = dx + dy;

    let acc = 0;
    while (true) {
      if (!(x0 < 0 || y0 < 0 || x0 >= wh || y0 >= wh)) {
        if (acc === 0) img[x0 + y0 * wh] = largest;
      }
      if (x0 === x1 && y0 === y1) break;
      const e2 = 2 * err;
      if (e2 >= dy) {
        err += dy;
        x0 += sx;
      }
      if (e2 <= dx) {
        err += dx;
        y0 += sy;
      }
      if (!!dashGap) {
        if (acc !== dashGap) acc++;
        else acc = 0;
      }
    }
  };
  const wh = res1.images[Object.keys(res1.images)[0]].plane.length ** 0.5;
  const borderCount = Math.round(
    20 /
      ((centers[0][0] - centers[1][0]) ** 2 +
        (centers[0][1] - centers[1][1]) ** 2 +
        (centers[0][2] - centers[1][2]) ** 2) **
        0.5,
  );
  linePoints.forEach((pts, index) => {
    const x0 = Math.round(((pts[0][0] - xMin) / (xMax - xMin)) * wh);
    const y0 = Math.round(((pts[0][1] - yMin) / (yMax - yMin)) * wh);
    const x1 = Math.round(((pts[1][0] - xMin) / (xMax - xMin)) * wh);
    const y1 = Math.round(((pts[1][1] - yMin) / (yMax - yMin)) * wh);
    drawLine(
      res1.images[Object.keys(res1.images)[0]].plane,
      x0,
      y0,
      x1,
      y1,
      wh,
      index % borderCount == 0 || index == linePoints.length - 1 ? 0 : 11,
    );
  });

  const res2 = await captureChamberPlane({
    viewportId,
    chamberViewProps,
    thickness,
    thicknessMethod,
    origin: volume.origin,
    pixelSpacing: spacing,
    scalarArray: Int16Array.from(scalar),
    dimension,
    windowLevel,
    windowWidth,
    // largest,
    smallest,
    img: imgURL,
    onProgress,
    isScout: false,
    isHalf: false,
  });
  return {
    ...res2,
    scoutInfo: res1.common,
    scout: Object.values(res1.images)[0],
  };
}
export async function captureChamberPlane({
  viewportId,
  chamberViewProps,
  thickness,
  thicknessMethod,
  // direction,
  origin,
  pixelSpacing,
  scalarArray,
  dimension,
  windowLevel,
  windowWidth,
  // largest,
  smallest,
  img,
  onProgress,
  isScout,
  isHalf,
}: {
  viewportId: PanelComponentKeyType;
  chamberViewProps: ChamberViewProps;
  thickness: number;
  thicknessMethod: ThicknessMethodType;
  // direction:[number,number,number,number,number,number,number,number,number]|Float32Array<ArrayBufferLike>,
  origin: [number, number, number];
  pixelSpacing: [number, number, number];
  scalarArray: Int16Array;
  dimension: [number, number, number];
  windowLevel: number;
  windowWidth: number;
  // largest: number,
  smallest: number;
  img?: boolean;
  onProgress?: ({
    loaded,
    total,
    progress,
  }: {
    loaded: boolean;
    total?: number;
    progress?: number;
  }) => void;
  isScout: boolean;
  isHalf: boolean;
}) {
  const interpolation =
    configStore.get().chamberCaptureInterpolation ?? 'linear';
  const {
    viewNormal,
    normal,
    centers,
    imageSize,
    alignAxis,
    rollRad,
    flipVertical,
    flipHorizontal,
  } = chamberViewProps;
  if (!viewNormal || !normal || !centers || !imageSize || !alignAxis)
    throw '없????';

  type n3 = [number, number, number];
  const zFlip = (v: n3): n3 => [v[0], v[1], -v[2]];
  const negate = (v: n3): n3 => [-v[0], -v[1], -v[2]];

  const psScale = [
    1,
    1,
    pixelSpacing[0] / pixelSpacing[2],
  ] as typeof pixelSpacing;
  const psScale2 = [1, 1, 1] as typeof pixelSpacing;
  const alignUp = isScout
    ? normal
    : alignFunction(viewNormal, normal, alignAxis).axis;
  let planeNormal = normalize(zFlip(viewNormal as n3));

  const right = getOrthoNormal(alignUp as n3, viewNormal as n3);
  let tangent1 = normalize(zFlip(right as n3));
  let tangent2 = normalize(zFlip(alignUp as n3));

  const rightRight = getOrthoNormal(alignUp as n3, planeNormal as n3);

  let tangent1_ = rightRight;
  let tangent2_ = tangent2;

  if (isScout) {
    tangent1_ = negate(tangent1_);
    tangent2_ = zFlip(tangent2_);
  } else {
    if (viewportId === 'Axial') {
      tangent1_ = zFlip(tangent1_);
    } else {
      tangent1_ = negate(tangent1_);
      tangent2_ = zFlip(tangent2_);
    }
  }

  //scale
  tangent1 = scalePoint3(tangent1, psScale);
  tangent2 = scalePoint3(tangent2, psScale);
  tangent1_ = scalePoint3(tangent1_, psScale);
  tangent2_ = scalePoint3(tangent2_, psScale);

  tangent1 = normalize(tangent1);
  tangent2 = normalize(tangent2);
  tangent1_ = normalize(tangent1_);
  tangent2_ = normalize(tangent2_);

  //회전먼저
  let tangent11 = tangent1_;
  let tangent22 = tangent2;

  // 뒤틀린 황천
  if (!isScout) {
    console.log(viewportId, dot(tangent11, [1, 0, 0]));
    if (
      (viewportId === 'FourChamber' || viewportId === 'ShortAxis') &&
      dot(tangent11, [1, 0, 0]) < 0
    ) {
      tangent11 = negate(tangent11);
      tangent1_ = negate(tangent1_);
    }
  }

  if (rollRad !== 0) {
    const n = getOrthoNormal(tangent1, tangent2);
    const n_ = getOrthoNormal(tangent1_, tangent2_);
    // const nn = getOrthoNormal(tangent1,tangent2);

    tangent1 = rotateAroundAxis(tangent1, n, rollRad);
    tangent2 = rotateAroundAxis(tangent2, n, rollRad);

    tangent1_ = rotateAroundAxis(tangent1_, n_, -rollRad);
    tangent2_ = rotateAroundAxis(tangent2_, n_, -rollRad);

    tangent11 = rotateAroundAxis(tangent11, n, rollRad);
    tangent22 = rotateAroundAxis(tangent22, n, rollRad);
  }

  tangent1_ = normalize(flipHorizontal ? negate(tangent1_) : tangent1_);
  tangent1 = normalize(flipHorizontal ? negate(tangent1) : tangent1);
  tangent11 = normalize(flipHorizontal ? negate(tangent11) : tangent11);
  tangent2_ = normalize(flipVertical ? negate(tangent2_) : tangent2_);
  tangent2 = normalize(flipVertical ? negate(tangent2) : tangent2);
  tangent22 = normalize(flipVertical ? negate(tangent22) : tangent22);

  if (isHalf) {
    tangent1 = tangent1.map((v) => v * 0.5) as typeof tangent1;
    tangent2 = tangent2.map((v) => v * 0.5) as typeof tangent2;
  }
  const direction: [
    number,
    number,
    number,
    number,
    number,
    number,
    number,
    number,
    number,
  ] = [-1, 0, 0, 0, -1, 0, 0, 0, -1];

  const scale = isHalf ? 2 : 1;
  // const scaledPlaneNormalLength = length(scalePoint3(planeNormal, pixelSpacing))*scale;
  // const scaledTangent1Length = length(scalePoint3(tangent1_, pixelSpacing));
  // const scaledTangent2Length = length(scalePoint3(tangent2_, pixelSpacing));

  const scaledPlaneNormalLength = pixelSpacing[0] * scale; //length(scalePoint3(planeNormal, pixelSpacing))*scale;
  const scaledTangent1Length = pixelSpacing[0]; //length(scalePoint3(tangent1_, pixelSpacing));
  const scaledTangent2Length = pixelSpacing[1]; //length(scalePoint3(tangent2_, pixelSpacing));

  // dicom의 ps태그가 imageTag로만 들어가기에, 고정된값으로 해야함
  const width = Math.floor(imageSize / pixelSpacing[0]) + 1;
  const height = Math.floor(imageSize / pixelSpacing[1]) + 1;
  // const [widthScale,heightSclae] = [width * 0.5 * scaledTangent1Length,height * 0.5 * scaledTangent2Length];
  // const [t1Scale,t2Scale] = [tangent1_.map(v=>v*widthScale),tangent2_.map(v=>v*heightSclae)];
  const numSamples = Math.ceil(thickness / scaledPlaneNormalLength);
  // IPP
  const getIPP = (flipCenter: [number, number, number]) => {
    const center = correctCenter(flipCenter, direction, origin, pixelSpacing);
    return [
      center[0] -
        tangent1_[0] * (width * 0.5 * scaledTangent1Length * psScale2[0]) -
        tangent2_[0] * (height * 0.5 * scaledTangent2Length * psScale2[0]),
      center[1] -
        tangent1_[1] * (width * 0.5 * scaledTangent1Length * psScale2[1]) -
        tangent2_[1] * (height * 0.5 * scaledTangent2Length * psScale2[1]),
      center[2] -
        tangent1_[2] * (width * 0.5 * scaledTangent1Length * psScale2[2]) -
        tangent2_[2] * (height * 0.5 * scaledTangent2Length * psScale2[2]),
    ] as [number, number, number];
  };

  const negativeHalfThickness = -numSamples * 0.5;

  const planeSize: [number, number] = [width, height];

  const res: {
    common: CaptureChamberCommonProps;
    images: Record<string, CaptureChamberImageProps>;
    scoutInfo?: CaptureChamberCommonProps;
    scout?: CaptureChamberImageProps;
  } = {
    common: {
      label: viewportId,
      width,
      height,
      orientation: [
        tangent11[0],
        tangent11[1],
        -tangent11[2],
        tangent22[0],
        tangent22[1],
        -tangent22[2],
      ],
      windowLevel,
      windowWidth,
      sliceThickness: thickness,
    },
    images: {},
  };

  // console.time('내용? 아직 안 봤어요')
  const scaleTangent1 =
    pixelSpacing[0] / length(scalePoint3(normalize(tangent1), pixelSpacing));
  const scaleTangent2 =
    pixelSpacing[1] / length(scalePoint3(normalize(tangent2), pixelSpacing));
  const scaledTangent1 = tangent1.map(
    (v) => v * scaleTangent1,
  ) as typeof tangent1;
  const scaledTangent2 = tangent2.map(
    (v) => v * scaleTangent2,
  ) as typeof tangent2;
  let progress = 0;
  const tasks: Promise<any>[] = [];
  for (let c = 0; c < centers.length; c++) {
    const voxelCenter = centers[c].map((v, i) => {
      return ((i === 2 ? 1 : -1) * (v - origin[i])) / (pixelSpacing[i] * scale);
    }) as [number, number, number];

    // console.log(getIPP(centers[c]))
    // continue;
    tasks.push(
      runWorkerTask({
        type: 'chamberPlane',
        scalarArray,
        voxelCenter,
        planeNormal,
        tangent1: scaledTangent1,
        tangent2: scaledTangent2,
        dimension,
        thicknessMethod,
        interpolation,
        planeSize,
        numSamples,
        negativeHalfThickness,
        smallest,
        cid: c,
        // fillMethod,
        // plane:planes[c]
      }).then(({ plane, cid }) => {
        progress++;
        if (onProgress)
          onProgress({ loaded: false, total: centers.length, progress });

        const resId = cid + Number(!isScout);
        // 어차피 type은 common이 알고있다.
        res.images[`${resId}`] = {
          position: getIPP(centers[cid]),
          plane,
        };
        // res[c].plane = plane;

        //쏴보기
        if (img) {
          console.time('쏴요');
          const upper = windowLevel + windowWidth * 0.5;
          const lower = windowLevel - windowWidth * 0.5;
          const localClamping = (val: number) =>
            val < lower ? lower : val > upper ? upper : val;
          const rcpDenom = 1.0 / (upper - lower);
          const localNormalize = (val: number) => (val - lower) * rcpDenom;
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          const arr = new Uint8ClampedArray(width * height * 4);

          for (let j = 0; j < height; j++) {
            for (let i = 0; i < width; i++) {
              const planeId = i + j * width;
              const id = planeId * 4;
              // const id = ((width - i-1) + j*width)*4;
              const val = localNormalize(localClamping(plane[planeId])) * 255;
              arr[id + 0] = val;
              arr[id + 1] = val;
              arr[id + 2] = val;
              arr[id + 3] = 255;
            }
          }
          const imageData = new ImageData(arr, width, height);
          ctx?.putImageData(imageData, 0, 0);
          const image = canvas.toDataURL('image/jpeg');
          // res[c].img = image;
          res.images[resId] = {
            ...res.images[resId],
            img: image,
          };
          // console.log(getIPP(centers[c]))
          // console.log(image)
          console.timeEnd('쏴요');
        }
        return true;
      }),
    );

    // console.log('끝나')
  }

  await Promise.all(tasks);
  if (onProgress)
    onProgress({
      loaded: true,
      total: centers.length,
      progress: centers.length,
    });
  // console.timeEnd('내용? 아직 안 봤어요')
  return res;
}
